/**
 * kuvr-track.js — first-party ad-tracking script (M11 Sprint 5, tracker v0.4.0)
 *
 * Deployed to the Shopify theme as an asset via shopify-theme-push.sh
 * (dev theme 134846447755 first — NEVER straight to live; every deploy needs
 * Duncan's explicit approval).
 *
 * Spec: kuvrnexus-app/.planning/ads/ARCHITECTURE.md
 *   §2.1 identity   §2.2 acquisition + internal tagging   §2.3 taxonomy v2
 *   §2.5 transport  §2.6 consent tiers
 *
 * CONSENT TIERS (spec §2.6, revised 2026-07-06):
 *   1. Essential (always, all regions): identity cookies + cart-attribute
 *      stamping for cart→order join. No behavioural events, no click-IDs.
 *   2. Advertising/behavioural: all taxonomy events + click-ID capture.
 *      EEA/UK/CH → only after Shopify Customer Privacy API reports consent;
 *      suppressed on decline. US & rest of world → on by default (opt-out
 *      regime, disclosed in privacy policy).
 *   NEVER programmatically grant consent on the visitor's behalf.
 *
 * Sprint 1 scope: page/content/session events, section_dwell, scroll_depth,
 * declarative data-kuvr-track instrumentation, visibility, web_vitals,
 * js/net errors, batching/sendBeacon, internal-traffic tagging (TR-44).
 * Sprint 2 adds: DPO configurator capture (quote_generated + interaction
 * events, TR-03/33/35) and free-design lead interception (TR-08). Checkout
 * events come from the Web Pixel (theme-assets/kuvr-pixel.js), not this file.
 * Sprint 3 adds: Microsoft Clarity load + identify/tags (TR-06), behavioural
 * tier only — inherits the same consent gate as taxonomy events.
 * Sprint 5 adds: quote-recovery prompt (TR-37 — post-quote idle / desktop
 * exit-intent, frequency-capped, NEVER pre-quote) creating `recovery` leads;
 * expert-contact lead path (TR-36); sendLead generalised to all three types.
 */
(function () {
  'use strict';

  // ------------------------------------------------------------------ config
  var CONFIG = {
    endpoint: 'https://nexus.kuvr.co/api/track/events',
    leadEndpoint: 'https://nexus.kuvr.co/api/track/lead',
    // TR-73: MX check. If this key is missing the lookup fetches a relative
    // 'undefined' URL, throws, and kuvrDomainReachable fails OPEN — the check
    // goes inert with no error anywhere. That is exactly what happened once.
    validateEmailEndpoint: 'https://nexus.kuvr.co/api/track/validate-email',
    trackerVersion: '0.4.0',
    clarityProjectId: 'xld4baffek', // TR-06 — Bishop Covers storefront project
    flushMaxEvents: 10,
    flushIntervalMs: 5000,
    maxPayloadBytes: 32 * 1024,
    maxErrorsPerSession: 5,
    sessionTimeoutMs: 30 * 60 * 1000,
    cookieDays: 400,
    // TR-37 quote recovery: idle-after-quote before prompting; cap 1 prompt
    // per visitor per 7 days (localStorage); never pre-quote (spec §5.6).
    recoveryIdleMs: 45000,
    recoveryCapDays: 7,
    // 31 consent regions: EEA + UK + CH (ISO 3166-1 alpha-2)
    consentRegions: [
      'AT','BE','BG','HR','CY','CZ','DK','EE','FI','FR','DE','GR','HU','IS',
      'IE','IT','LV','LI','LT','LU','MT','NL','NO','PL','PT','RO','SK','SI',
      'ES','SE','GB','CH'
    ]
  };

  // ------------------------------------------------------------- tiny utils
  function uuid() {
    if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
  }
  function getCookie(name) {
    var m = document.cookie.match('(?:^|; )' + name + '=([^;]*)');
    return m ? decodeURIComponent(m[1]) : null;
  }
  function setCookie(name, value, days) {
    var d = new Date();
    d.setTime(d.getTime() + days * 86400000);
    document.cookie =
      name + '=' + encodeURIComponent(value) +
      '; expires=' + d.toUTCString() + '; path=/; SameSite=Lax; Secure';
  }
  function ls(key, value) {
    try {
      if (arguments.length === 2) { localStorage.setItem(key, value); return value; }
      return localStorage.getItem(key);
    } catch (e) { return null; }
  }
  function param(name) {
    var m = location.search.match(new RegExp('[?&]' + name + '=([^&]*)'));
    return m ? decodeURIComponent(m[1].replace(/\+/g, ' ')) : null;
  }
  function normalisePath(p) { return p.replace(/^\/en-us(\/|$)/, '/'); }
  function deviceType() {
    return /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent) ? 'mobile' : 'desktop';
  }

  // -------------------------------------------------- internal traffic (TR-44)
  if (param('kuvr_internal') === '1') setCookie('_kuvr_internal', '1', CONFIG.cookieDays);
  var IS_INTERNAL =
    getCookie('_kuvr_internal') === '1' ||
    param('test_mode') === '1' ||
    param('preview_theme_id') !== null ||
    (window.Shopify && Shopify.theme && Shopify.theme.role &&
      Shopify.theme.role !== 'main');

  // ------------------------------------------------------------ consent tiers
  var COUNTRY =
    (window.Shopify && Shopify.country) ||
    (function () {
      var m = document.documentElement.lang || '';
      return m.indexOf('-') > 0 ? m.split('-')[1].toUpperCase() : '';
    })();
  var IN_CONSENT_REGION = CONFIG.consentRegions.indexOf(COUNTRY) !== -1;

  var consent = {
    status: IN_CONSENT_REGION ? 'unknown' : 'not_required',
    basis: IN_CONSENT_REGION ? 'consent' : 'opt_out_regime',
    timestamp: null,
    version: '2026-07-05' // consent-policy version (spec §2.6)
  };

  function behaviouralAllowed() {
    if (!IN_CONSENT_REGION) return true; // US/RoW: default-on, opt-out regime
    return consent.status === 'granted';
  }

  function readShopifyConsent() {
    try {
      var cp = window.Shopify && Shopify.customerPrivacy;
      if (!cp) return;
      var allowed =
        (typeof cp.analyticsProcessingAllowed === 'function' && cp.analyticsProcessingAllowed()) ||
        (typeof cp.marketingAllowed === 'function' && cp.marketingAllowed());
      var saleOfDataRegion = typeof cp.saleOfDataRegion === 'function' ? cp.saleOfDataRegion() : false;
      if (IN_CONSENT_REGION) {
        // VERIFIED LIVE 2026-07-12 (twice): pre-decision, BOTH
        // analyticsProcessingAllowed() (returns true) AND shouldShowBanner()
        // (returned false with the banner visibly up) are unreliable on this
        // store — declined sessions shipped events under v0.2.x AND v0.3.2.
        // The only trustworthy signal is the EXPLICIT decision record:
        // currentVisitorConsent().analytics/.marketing = 'yes' | 'no' | ''
        // ('' = no decision yet). Grant/decline ONLY on 'yes'/'no'.
        var cv = null;
        try {
          cv = typeof cp.currentVisitorConsent === 'function' ? cp.currentVisitorConsent() : null;
        } catch (e2) { cv = null; }
        if (cv && (cv.analytics === 'yes' || cv.marketing === 'yes')) {
          consent.status = 'granted';
          consent.timestamp = Date.now();
        } else if (cv && (cv.analytics === 'no' || cv.marketing === 'no')) {
          consent.status = 'declined';
        } else if (cv) {
          consent.status = 'unknown'; // API present, no decision recorded yet
        } else {
          // currentVisitorConsent unavailable — conservative fallback
          var pending = typeof cp.shouldShowBanner === 'function' && cp.shouldShowBanner();
          if (pending) {
            consent.status = 'unknown';
          } else if (allowed) {
            consent.status = 'granted';
            consent.timestamp = Date.now();
          } else {
            consent.status = 'declined';
          }
        }
      } else if (saleOfDataRegion) {
        // US state opt-out regions: honour an explicit opt-out
        if (typeof cp.saleOfDataAllowed === 'function' && !cp.saleOfDataAllowed()) {
          consent.status = 'declined';
          consent.basis = 'opt_out_regime';
        }
      }
    } catch (e) { /* defensive — consent read must never break the page */ }
  }
  readShopifyConsent();
  document.addEventListener('visitorConsentCollected', function () {
    var was = behaviouralAllowed();
    readShopifyConsent();
    if (!was && behaviouralAllowed()) startBehaviouralTracking(); // upgrade mid-session
  });
  // Shopify.customerPrivacy loads ASYNC — if the tracker script wins the race
  // (verified live 2026-07-12: GB visitor stuck at 'unknown', all events
  // silently dropped), re-read consent on a short poll until the API appears
  // and the state settles. visitorConsentCollected only covers banner clicks,
  // not late API availability.
  (function pollConsent() {
    var tries = 0;
    var iv = setInterval(function () {
      tries++;
      var was = behaviouralAllowed();
      readShopifyConsent();
      if (!was && behaviouralAllowed()) startBehaviouralTracking();
      if (behaviouralAllowed() || tries >= 20) clearInterval(iv); // ≤10s
    }, 500);
  })();

  // US opt-out: declined in an opt-out region also suppresses behavioural tier
  function trackingActive() {
    if (IN_CONSENT_REGION) return behaviouralAllowed();
    return consent.status !== 'declined';
  }

  // --------------------------------------------------------------- identity
  // Essential tier — runs in ALL regions regardless of consent (spec §2.6 tier 1).
  var visitorId = getCookie('_kuvr_vid') || ls('_kuvr_vid');
  var isNewVisitor = !visitorId;
  if (!visitorId) visitorId = uuid();
  setCookie('_kuvr_vid', visitorId, CONFIG.cookieDays);
  ls('_kuvr_vid', visitorId);

  var now = Date.now();
  var sessionId = ls('_kuvr_sid');
  var lastActivity = parseInt(ls('_kuvr_sla') || '0', 10);
  var sessionStartedAt = parseInt(ls('_kuvr_sst') || '0', 10);
  var isNewSession = !sessionId || now - lastActivity > CONFIG.sessionTimeoutMs;
  if (isNewSession) {
    sessionId = uuid();
    sessionStartedAt = now;
    ls('_kuvr_sid', sessionId);
    ls('_kuvr_sst', String(now));
  }
  ls('_kuvr_sla', String(now));

  var landingVariant = param('view') || null;

  // ------------------------------------------------------------- acquisition
  // Click-IDs are advertising-tier data: only captured when the behavioural
  // tier is permitted (spec §2.6 tier 2).
  function buildTouch() {
    if (!trackingActive()) return null;
    var t = {
      source: param('utm_source'),
      medium: param('utm_medium'),
      campaign: param('utm_campaign'),
      content: param('utm_content'),
      term: param('utm_term'),
      gclid: param('gclid'),
      wbraid: param('wbraid'),
      gbraid: param('gbraid'),
      fbclid: param('fbclid'),
      fbp: getCookie('_fbp'),
      fbc: getCookie('_fbc'),
      landingPath: normalisePath(location.pathname),
      landingVariant: landingVariant,
      referrer: document.referrer ? document.referrer.slice(0, 512) : null,
      ts: now
    };
    var hasSignal = t.source || t.medium || t.campaign || t.gclid || t.wbraid ||
                    t.gbraid || t.fbclid || t.referrer;
    // Send a touch on every new session that carries any acquisition signal,
    // and always on a brand-new visitor (server enforces write-once firstTouch).
    if (isNewVisitor || (isNewSession && hasSignal)) return t;
    return null;
  }

  // ---------------------------------------------------------------- transport
  var queue = [];
  var flushTimer = null;
  var lastEventName = null;
  var touchToSend = buildTouch();

  function envelope(events) {
    return {
      visitor: {
        visitor_id: visitorId,
        device: deviceType(),
        country: COUNTRY || undefined,
        consent: {
          status: consent.status,
          basis: consent.basis,
          timestamp: consent.timestamp || undefined,
          version: consent.version
        },
        touch: touchToSend || undefined
      },
      session: {
        session_id: sessionId,
        started_at: sessionStartedAt,
        entry_path: normalisePath(location.pathname),
        landing_variant: landingVariant,
        device: deviceType()
      },
      events: events,
      tracker_version: CONFIG.trackerVersion,
      internal: !!IS_INTERNAL,
      hp: ''
    };
  }

  function send(payload, useBeacon) {
    var body = JSON.stringify(payload);
    if (body.length > CONFIG.maxPayloadBytes) {
      // split oversize batches in half and retry
      if (payload.events.length > 1) {
        var mid = Math.ceil(payload.events.length / 2);
        send(envelope(payload.events.slice(0, mid)), useBeacon);
        send(envelope(payload.events.slice(mid)), useBeacon);
      }
      return;
    }
    var sent = false;
    if (useBeacon && navigator.sendBeacon) {
      try {
        sent = navigator.sendBeacon(
          CONFIG.endpoint,
          new Blob([body], { type: 'application/json' })
        );
      } catch (e) { sent = false; }
    }
    if (!sent) {
      try {
        fetch(CONFIG.endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: body,
          keepalive: true,
          credentials: 'omit'
        }).catch(function () {});
      } catch (e) { /* never break the page */ }
    }
    touchToSend = null; // touch rides along once, on the first batch
  }

  function flush(useBeacon) {
    if (queue.length === 0) return;
    var events = queue.splice(0, queue.length);
    while (events.length > 0) {
      send(envelope(events.splice(0, CONFIG.flushMaxEvents)), useBeacon);
    }
    if (flushTimer) { clearTimeout(flushTimer); flushTimer = null; }
  }

  function track(name, props) {
    if (!trackingActive()) return; // behavioural tier suppressed
    lastEventName = name;
    ls('_kuvr_sla', String(Date.now()));
    queue.push({
      event_id: uuid(),
      name: name,
      ts: Date.now(),
      props: props || {},
      product_id: PRODUCT_ID || undefined,
      page_path: normalisePath(location.pathname),
      landing_variant: landingVariant || undefined
    });
    if (queue.length >= CONFIG.flushMaxEvents) {
      flush(false);
    } else if (!flushTimer) {
      flushTimer = setTimeout(function () { flushTimer = null; flush(false); },
        CONFIG.flushIntervalMs);
    }
  }
  // expose for theme snippets + Sprint 2 DPO glue
  window.kuvrTrack = track;

  // ------------------------------------------------------------ page context
  var SHOPIFY_META = (window.ShopifyAnalytics && ShopifyAnalytics.meta) || {};
  var PAGE = SHOPIFY_META.page || {};
  var PRODUCT_ID = PAGE.resourceId ? String(PAGE.resourceId) :
    (SHOPIFY_META.product && SHOPIFY_META.product.id ? String(SHOPIFY_META.product.id) : null);
  // The quote email rendered the raw product id ("6606424014987 x 1") in its line item
  // AND its subject, because this file read no title at all — `product: PRODUCT_ID` was
  // the only product identity in the payload, so Klaviyo had nothing else to render.
  // theme.liquid now emits window.KUVR_PRODUCT on product pages; fall back through
  // ShopifyAnalytics and finally the document title so this can never end up blank.
  var KP = window.KUVR_PRODUCT || {};
  var PRODUCT_TITLE = KP.title ||
    (SHOPIFY_META.product && SHOPIFY_META.product.variants && SHOPIFY_META.product.variants[0] &&
      String(SHOPIFY_META.product.variants[0].name || '').split(' - ')[0]) ||
    (document.title ? document.title.split(/[|–—]/)[0].trim() : '') || null;
  var PRODUCT_URL = KP.url || (location.origin + location.pathname);
  var PRODUCT_IMAGE = KP.image || null;
  var IS_PRODUCT_PAGE = PAGE.pageType === 'product' || !!SHOPIFY_META.product;

  // ------------------------------------------------ essential: cart stamping
  // Underscore-prefixed attributes are hidden from the customer but land on the
  // order (TR-01). Runs in the ESSENTIAL tier — session continuity, not profiling.
  var cartStamped = false;
  function stampCart() {
    if (cartStamped) return;
    cartStamped = true;
    try {
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          // _kuvr_int lets the Web Pixel mark checkout events from internal
          // sessions (TR-44) — the pixel sandbox cannot see cookies/theme role.
          attributes: IS_INTERNAL
            ? { _kuvr_vid: visitorId, _kuvr_sid: sessionId, _kuvr_int: '1' }
            : { _kuvr_vid: visitorId, _kuvr_sid: sessionId }
        }),
        credentials: 'same-origin',
        keepalive: true
      }).catch(function () { cartStamped = false; });
    } catch (e) { cartStamped = false; }
  }

  // ------------------------------------------- Microsoft Clarity (TR-06, §8)
  // Behavioural tier ONLY: loaded exclusively from startBehaviouralTracking(),
  // so consent gating is inherited (EEA/UK/CH consent, opt-out elsewhere).
  // No deep-link exists (AD0): Nexus locates replays by filtering the
  // session_id custom tag in the Clarity UI. Tags ≤255 chars, ≤128/page.
  var clarityLoaded = false;
  function claritySet(key, value) {
    try {
      if (clarityLoaded && window.clarity) window.clarity('set', key, String(value).slice(0, 255));
    } catch (e) { /* Clarity must never break the page */ }
  }
  function loadClarity() {
    if (clarityLoaded || !CONFIG.clarityProjectId) return;
    clarityLoaded = true;
    try {
      // official snippet — queues calls until the tag script arrives
      (function (c, l, a, r, i, t, y) {
        c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments); };
        t = l.createElement(r); t.async = 1; t.src = 'https://www.clarity.ms/tag/' + i;
        y = l.getElementsByTagName(r)[0];
        if (y && y.parentNode) { y.parentNode.insertBefore(t, y); }
        else { (l.head || l.documentElement).appendChild(t); }
      })(window, document, 'clarity', 'script', CONFIG.clarityProjectId);
      if (consent.status === 'granted') window.clarity('consent'); // cookie use only on explicit grant
      window.clarity('identify', visitorId, sessionId);
      claritySet('visitor_id', visitorId);
      claritySet('session_id', sessionId);
      if (PRODUCT_ID) claritySet('product', PRODUCT_ID);
      var ph = location.pathname.match(/\/products\/([^\/?#]+)/);
      if (ph) claritySet('product_handle', ph[1]);
      if (landingVariant) claritySet('variant', landingVariant);
      if (IS_INTERNAL) claritySet('internal', 'true');
    } catch (e) { clarityLoaded = false; }
  }

  // ----------------------------------------------------- behavioural wiring
  var behaviouralStarted = false;
  function startBehaviouralTracking() {
    if (behaviouralStarted) return;
    behaviouralStarted = true;
    if (!touchToSend) touchToSend = buildTouch(); // consent arrived mid-session
    loadClarity(); // TR-06 — behavioural tier, same gate as events

    // --- page & product views
    track('page_viewed', { title: document.title.slice(0, 200) });
    if (IS_PRODUCT_PAGE) track('product_page_viewed', {});

    // --- scroll depth (25/50/75/100, once each)
    var marks = [25, 50, 75, 100];
    var fired = {};
    function onScroll() {
      var doc = document.documentElement;
      var max = doc.scrollHeight - window.innerHeight;
      if (max <= 0) return;
      var pct = Math.round(((window.scrollY || doc.scrollTop) / max) * 100);
      for (var i = 0; i < marks.length; i++) {
        if (pct >= marks[i] && !fired[marks[i]]) {
          fired[marks[i]] = true;
          track('scroll_depth', { pct: marks[i] });
        }
      }
    }
    window.addEventListener('scroll', onScroll, { passive: true });

    // --- section dwell (IntersectionObserver on [data-kuvr-section])
    if (window.IntersectionObserver) {
      var dwellStart = {};
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var name = entry.target.getAttribute('data-kuvr-section');
          if (!name) return;
          if (entry.isIntersecting) {
            dwellStart[name] = Date.now();
          } else if (dwellStart[name]) {
            var seconds = Math.round((Date.now() - dwellStart[name]) / 1000);
            delete dwellStart[name];
            if (seconds >= 1) track('section_dwell', { section: name, seconds: seconds });
          }
        });
      }, { threshold: 0.3 });
      document.querySelectorAll('[data-kuvr-section]').forEach(function (el) {
        io.observe(el);
      });
    }

    // --- declarative instrumentation (spec §2.3): one delegated listener.
    //     data-kuvr-track="modal:fabric_guide"  (+ data-kuvr-source="configurator")
    //     data-kuvr-track="modalclose:fabric_guide"
    //     data-kuvr-track="link:measuring_guide" (+ data-kuvr-section="...")
    //     data-kuvr-track="event:faq_opened"     (+ data-kuvr-props='{"question":"..."}')
    var modalOpenedAt = {};
    document.addEventListener('click', function (ev) {
      var el = ev.target && ev.target.closest && ev.target.closest('[data-kuvr-track]');
      if (!el) return;
      var spec = el.getAttribute('data-kuvr-track') || '';
      var idx = spec.indexOf(':');
      if (idx < 1) return;
      var kind = spec.slice(0, idx);
      var value = spec.slice(idx + 1);
      if (kind === 'modal') {
        modalOpenedAt[value] = Date.now();
        track('modal_opened', {
          modal: value,
          trigger_source: el.getAttribute('data-kuvr-source') || 'content_link'
        });
      } else if (kind === 'modalclose') {
        var dur = modalOpenedAt[value]
          ? Math.round((Date.now() - modalOpenedAt[value]) / 1000) : null;
        delete modalOpenedAt[value];
        track('modal_closed', { modal: value, open_duration: dur });
      } else if (kind === 'link') {
        track('link_clicked', {
          target: value,
          section: el.getAttribute('data-kuvr-section') || null
        });
      } else if (kind === 'event') {
        var props = {};
        try { props = JSON.parse(el.getAttribute('data-kuvr-props') || '{}'); }
        catch (e) { /* bad inline JSON — send without props */ }
        track(value, props);
      }
    }, true);

    // --- add to cart (form submit or named button)
    document.addEventListener('submit', function (ev) {
      var form = ev.target;
      if (form && form.action && form.action.indexOf('/cart/add') !== -1) {
        track('add_to_cart', { product_id: PRODUCT_ID });
        stampCart();
      }
    }, true);

    // --- visibility (tab-away = comparison-shopping signal)
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'hidden') {
        track('visibility_hidden', {});
        reportVitalsOnce();
        flush(true); // beacon — page may be going away
      } else {
        track('visibility_returned', {});
      }
    });

    // --- errors (capped per session)
    var errorCount = 0;
    window.addEventListener('error', function (ev) {
      if (errorCount >= CONFIG.maxErrorsPerSession) return;
      errorCount++;
      if (ev.target && ev.target !== window && (ev.target.src || ev.target.href)) {
        track('net_error', {
          resource: String(ev.target.src || ev.target.href).slice(0, 300),
          status: null
        });
      } else {
        track('js_error', {
          message: String(ev.message || 'unknown').slice(0, 300),
          source: String(ev.filename || '').slice(0, 300)
        });
      }
    }, true);
    window.addEventListener('unhandledrejection', function (ev) {
      if (errorCount >= CONFIG.maxErrorsPerSession) return;
      errorCount++;
      track('js_error', {
        message: 'unhandledrejection: ' + String(ev.reason || '').slice(0, 250),
        source: null
      });
    });

    // --- web vitals (native PerformanceObserver — no external lib)
    var vitals = { lcp: null, inp: null, cls: 0 };
    var vitalsReported = false;
    try {
      new PerformanceObserver(function (list) {
        var entries = list.getEntries();
        if (entries.length) vitals.lcp = Math.round(entries[entries.length - 1].startTime);
      }).observe({ type: 'largest-contentful-paint', buffered: true });
      new PerformanceObserver(function (list) {
        list.getEntries().forEach(function (e) {
          if (!e.hadRecentInput) vitals.cls += e.value;
        });
      }).observe({ type: 'layout-shift', buffered: true });
      new PerformanceObserver(function (list) {
        list.getEntries().forEach(function (e) {
          var d = e.processingEnd - e.startTime;
          if (vitals.inp === null || d > vitals.inp) vitals.inp = Math.round(d);
        });
      }).observe({ type: 'event', buffered: true, durationThreshold: 40 });
    } catch (e) { /* older browsers */ }
    function reportVitalsOnce() {
      if (vitalsReported || vitals.lcp === null) return;
      vitalsReported = true;
      track('web_vitals', {
        lcp: vitals.lcp,
        inp: vitals.inp,
        cls: Math.round(vitals.cls * 1000) / 1000
      });
    }

    // --- DPO configurator capture (Sprint 2) — product pages only
    if (IS_PRODUCT_PAGE) initDpoCapture();

    // --- session end
    window.addEventListener('pagehide', function () {
      reportVitalsOnce();
      track('session_end', {
        duration: Math.round((Date.now() - sessionStartedAt) / 1000),
        last_event: lastEventName
      });
      flush(true); // sendBeacon on pagehide (spec §2.5)
    });
  }

  // ------------------------------------------------- DPO configurator (Sprint 2)
  // Spec §2.3/§2.4, TR-03/33/35. Evidence: .planning/ads/research/DPO-HOOKS.md +
  // STATE.md 2026-07-12 AD0 probe. CRITICAL facts honoured here:
  //   - dpoPriceUpdate fires on WINDOW via jQuery .trigger() — native
  //     addEventListener CANNOT see it; hook via dpoEvent.observe(window, ...).
  //   - Strictly PASSIVE capture: read the DOM, never call updatePrice() or
  //     synthesize events (recalc is heavy). Price is read from the displayed
  //     price node, currency from the currency plugin/Shopify globals.
  //   - Option IDs are per-product numeric — field map derived from DOM labels
  //     at runtime, never hard-coded.
  //   - Measurement fields render only after shape selection — all listeners
  //     are delegated; MutationObserver fallback if the event bus is absent.
  var DPO = {
    quoteDebounceMs: 1500,
    pollMs: 500,
    maxPolls: 60, // 30s — DPO loads from node1.itoris.com, can be slow
    // Generic unit-confusion bounds per unit (TR-35): [min, max] plausible for
    // any single cover dimension. Per-product ranges can tighten these later
    // via adConfig; these catch the classic cm-vs-inch/mm mixups.
    plausible: {
      cm: [3, 1200], m: [0.03, 12], mm: [30, 12000], 'in': [1, 470], ft: [0.1, 40]
    },
    maxValidationEvents: 10
  };

  function dpoContainer() {
    return document.querySelector('.dpo-container, .itoris-dpo-container, [class*="itoris-dpo"]');
  }

  // --- field map: label for any DPO field, derived at runtime (per-product IDs)
  function dpoLabelFor(el) {
    if (!el) return 'unknown';
    var row = el.closest && (el.closest('.itoris-dpo-row') || el.closest('.field') || el.closest('td'));
    var lbl = row && row.querySelector && row.querySelector('label');
    var text = lbl ? (lbl.innerText || lbl.textContent || '') : '';
    text = text.replace(/\*/g, '').replace(/\s+/g, ' ').trim();
    return text || el.name || el.id || 'unknown';
  }

  // Field label for option controls: the DOM label next to a swatch/radio is
  // its VALUE ("Counter"), not its field name ("Outdoor Kitchen Shape") —
  // verified live 2026-07-12. DPO's own config map has label->value, so the
  // field name is the key whose value matches the control's new value.
  function dpoFieldLabel(el, newValue) {
    var cfg = readDpoConfig();
    if (cfg && newValue) {
      var v = String(newValue).trim();
      var keys = Object.keys(cfg);
      for (var i = 0; i < keys.length; i++) {
        if (String(cfg[keys[i]]).trim() === v) return keys[i];
      }
    }
    return dpoLabelFor(el);
  }

  function isMeasurementInput(el) {
    return el && el.tagName === 'INPUT' && el.type === 'text' && /^options_\d+_text$/.test(el.id || '');
  }
  function isDpoField(el) {
    if (!el || !el.name) return false;
    return /^options\[\d+\]/.test(el.name) || /^options_\d+/.test(el.id || '');
  }

  function visibleMeasurementInputs() {
    var out = [];
    var nodes = document.querySelectorAll('input[type="text"][id^="options_"]');
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].id.indexOf('_text') !== -1 && nodes[i].offsetParent !== null) out.push(nodes[i]);
    }
    return out;
  }

  function readMeasurements() {
    var m = {};
    visibleMeasurementInputs().forEach(function (el) {
      var v = (el.value || '').trim();
      if (v) m[dpoLabelFor(el)] = v;
    });
    return m;
  }

  function allMeasurementsFilled() {
    var inputs = visibleMeasurementInputs();
    if (inputs.length === 0) return false;
    for (var i = 0; i < inputs.length; i++) {
      if (!(inputs[i].value || '').trim()) return false;
    }
    return true;
  }

  // Units: DPO shows per-field unit indicators; also a unit selector may exist.
  function readUnits() {
    var el = document.querySelector('.dpo-container [class*="unit"], .itoris-dpo-row [class*="unit"]');
    var t = el && el.textContent ? el.textContent.replace(/\s+/g, ' ').trim().toLowerCase() : '';
    var m = t.match(/\b(cm|mm|m|in|inch|inches|ft|feet)\b/);
    if (m) return m[1].replace(/inch(es)?/, 'in').replace(/feet/, 'ft');
    // fallback: scan field labels
    var lbls = document.querySelectorAll('.itoris-dpo-row label');
    for (var i = 0; i < lbls.length; i++) {
      var mm2 = (lbls[i].innerText || lbls[i].textContent || '').toLowerCase().match(/\((cm|mm|m|in|inches|ft)\)/);
      if (mm2) return mm2[1].replace('inches', 'in');
    }
    return null;
  }

  function readShape() {
    var radios = document.querySelectorAll('.dpo-container input[type="radio"]:checked, [class*="itoris-dpo"] input[type="radio"]:checked');
    for (var i = 0; i < radios.length; i++) {
      var label = dpoLabelFor(radios[i]).toLowerCase();
      if (label.indexOf('shape') !== -1 || radios[i].closest('.options-list')) {
        return radioValueLabel(radios[i]);
      }
    }
    return null;
  }

  // 🛑 DPO renders an option's SURCHARGE next to its label in the picker — "Snap buckles
  // + elasticated corners+£5.99", "2 zips+£55.00" (note: no separating space). Both the
  // swatch label and the <option> text were read verbatim, so the price was captured as
  // part of the customer's CHOICE. Shopify's own line-item properties are clean because
  // DPO writes the option VALUE, not the rendered label; only this tracker picked up the
  // display text. See .planning/BUG-2026-09-11-price-suffix-leaks-into-option-values.md.
  // The event must carry the choice, never its price — prices have their own fields and
  // already carry them correctly, and a glued-on price goes silently stale the first time
  // that surcharge changes.
  // Anchored at the END and REQUIRING a currency symbol, both deliberately: "Snap buckles
  // + elasticated corners" contains a legitimate internal " + ", and an option could
  // legitimately read "Counter + 2". Only a trailing SIGNED CURRENCY amount is a surcharge.
  var SURCHARGE_RE = /\s*\(?\s*[+\-−–]\s*[£$€]\s*[\d,]+(?:\.\d{1,2})?\s*\)?\s*$/;
  function stripSurcharge(v) {
    return String(v == null ? '' : v).replace(SURCHARGE_RE, '').trim();
  }

  function radioValueLabel(radio) {
    // DPO swatch radios: the human value is on the sibling label/swatch title
    var wrap = radio.closest('label') || radio.parentElement;
    var t = wrap && wrap.textContent ? stripSurcharge(wrap.textContent.replace(/\s+/g, ' ')) : '';
    if (t) return t.slice(0, 80);
    return radio.value || 'selected';
  }

  // The quote email listed the customer's choices in a scrambled order — Fabric type,
  // Length B, Shape, Material colour, Fasteners, Fixing type, Length C, Unit, Length A.
  // Cause: measurements and options are collected into two SEPARATE objects
  // (readMeasurements / readOptions), and readOptions itself does all <select>s first
  // and all radios second. Whatever merges them downstream cannot recover the order the
  // customer actually saw. This walks every visible DPO field ONCE in true DOM order and
  // emits an ARRAY, so the sequence is explicit and cannot be lost in an object.
  // Additive: `measurements` and `options` are still sent unchanged.
  // The field name as the CUSTOMER sees it, read from this product's own markup.
  // For a swatch/radio the adjacent label is the VALUE ("Zip split"), so walk up to
  // the enclosing .fieldset and take its first NON-OPTION label — DPO renders the
  // field's own label there ("Splits"). Verified live on /clear 2026-08-20:
  //   Unit of measure · Material · Material colour · Shape · Fixing type ·
  //   Customise eyelets · Customise turn buttons · Splits
  // Product-agnostic by construction: nothing here knows which product it is on.
  function fieldLabelFor(el) {
    var fs = el.closest && el.closest('.fieldset');
    if (fs) {
      var labels = fs.querySelectorAll('label');
      for (var i = 0; i < labels.length; i++) {
        var owner = labels[i].closest ? labels[i].closest('.field') : null;
        // .field.choice is an OPTION row; its label is the value, not the field name.
        if (owner && owner.className && owner.className.indexOf('choice') !== -1) continue;
        var t = (labels[i].innerText || labels[i].textContent || '')
                  .replace(/\*/g, '').replace(/\s+/g, ' ').trim();
        if (t) return t;
      }
    }
    return null;
  }

  function readOrderedConfig() {
    var out = [];
    var seen = {};
    var cand = [];
    var nodes = document.querySelectorAll(
      '.dpo-container input, .dpo-container select, .dpo-container textarea,' +
      '[class*="itoris-dpo"] input, [class*="itoris-dpo"] select, [class*="itoris-dpo"] textarea');
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      try {
        if (!isDpoField(el)) continue;
        if (el.type === 'hidden' || el.type === 'file') continue;
        if (el.type === 'radio' || el.type === 'checkbox') {
          if (!el.checked) continue;
        } else if (el.offsetParent === null) continue;

        // 🛑 DPO seeds its note textareas with the PROMPT TEXT as a real value, marked
        // with .default-message (it drops the class the moment the customer types).
        // Reading .value on an untouched field therefore returns the prompt, which is
        // how a customer was emailed "Split placement instructions: Please provide
        // instructions regarding location and height of splits...". Untouched = skip.
        if (el.tagName === 'TEXTAREA' && el.className &&
            el.className.indexOf('default-message') !== -1) continue;

        var value;
        if (el.type === 'radio' || el.type === 'checkbox') value = radioValueLabel(el);
        else if (el.tagName === 'SELECT') {
          var so = el.options[el.selectedIndex];
          value = so ? stripSurcharge(so.text || so.value || '') : '';
          if (/^(please select|select|---)$/i.test(value)) continue;
        } else value = (el.value || '').trim();
        if (!value) continue;

        // For a swatch/radio the adjacent DOM label is the VALUE ("Trapezium"), not the
        // field name ("Shape") — so dpoLabelFor gave "Trapezium: Trapezium" in the email.
        // dpoFieldLabel reverse-looks-up the real field name from DPO's own config map.
        var label;
        if (el.type === 'radio' || el.type === 'checkbox') {
          label = dpoFieldLabel(el, value);
          // dpoFieldLabel falls back to the adjacent label, which for a swatch is the
          // VALUE — that is how "Trapezium: Trapezium" reached a customer. If it came
          // back echoing the value, take the real field name from the DOM instead.
          if (!label || label === value) label = fieldLabelFor(el) || label;
        } else {
          label = dpoLabelFor(el) || fieldLabelFor(el);
        }
        if (!label) continue;
        // 🛑 SORT BY VISUAL POSITION, NOT DOM POSITION. Verified live 2026-08-20 on
        // the Outdoor Kitchen page: DPO's DOM order is
        //   Centimetres > Metres > Inches > Counter > Counter+Grill > ...
        // while the visual order is completely different. Walking the DOM therefore
        // produced a sequence the customer never saw — the emailed summary read
        // Unit > Elasticated corners > Length B > Fabric > Shape > Length C > Length A.
        // Position is taken from the field ROW because swatch inputs are visually
        // hidden and would all report top: 0.
        var row = (el.closest && (el.closest('.field') || el.closest('.itoris-dpo-row') ||
                   el.closest('li') || el.closest('tr'))) || el.parentElement || el;
        var rect = row.getBoundingClientRect();
        cand.push({
          top: Math.round(rect.top + (window.pageYOffset || 0)),
          left: Math.round(rect.left + (window.pageXOffset || 0)),
          label: label,
          // 120 chars truncated a genuine instruction mid-word ("...to check and ap").
          value: String(value).slice(0, 400)
        });
      } catch (e) { /* one bad field must never drop the whole quote */ }
    }
    // A customer can request a quote before finishing — that is deliberate, since a
    // half-built configuration is exactly the lead worth recovering. But silently
    // omitting the unfinished fields made the email look complete when it was not,
    // and the resume link then landed on a page that still would not add to cart.
    // Required-but-unset fields are therefore INCLUDED and marked, in their proper
    // visual position, so the email reads as a to-do rather than a finished spec.
    try {
      var setLabels = {};
      for (var si = 0; si < cand.length; si++) setLabels[cand[si].label] = 1;
      var groups = document.querySelectorAll('#itoris_dynamicproductoptions .fieldset');
      for (var gi = 0; gi < groups.length; gi++) {
        var g = groups[gi];
        if (!g.querySelector('.required, [required]')) continue;      // not a required field
        if (g.offsetParent === null || !g.getClientRects().length) continue; // hidden branch
        var inputs = g.querySelectorAll('input, select, textarea');
        if (!inputs.length) continue;
        var isSet = false;
        for (var ii = 0; ii < inputs.length; ii++) {
          var f = inputs[ii];
          if (f.type === 'hidden' || f.type === 'file') continue;
          if (f.type === 'radio' || f.type === 'checkbox') { if (f.checked) { isSet = true; break; } }
          else if (f.tagName === 'TEXTAREA' && (f.className || '').indexOf('default-message') !== -1) continue;
          else if ((f.value || '').trim()) { isSet = true; break; }
        }
        if (isSet) continue;
        var glabel = fieldLabelFor(g.querySelector('input, select, textarea')) ||
                     (g.querySelector('label') ? g.querySelector('label').textContent : '');
        glabel = String(glabel).replace(/\*/g, '').replace(/\s+/g, ' ').trim();
        if (!glabel || setLabels[glabel]) continue;
        setLabels[glabel] = 1;
        var grect = g.getBoundingClientRect();
        cand.push({
          top: Math.round(grect.top + (window.pageYOffset || 0)),
          left: Math.round(grect.left + (window.pageXOffset || 0)),
          label: glabel,
          value: 'Not chosen yet'
        });
      }
    } catch (e) { /* never let this block a capture */ }

    cand.sort(function (a, b) { return (a.top - b.top) || (a.left - b.left); });
    for (var ci = 0; ci < cand.length; ci++) {
      if (seen[cand[ci].label]) continue;
      seen[cand[ci].label] = 1;
      out.push({ label: cand[ci].label, value: cand[ci].value });
    }
    return out.length ? out : undefined;
  }

  function readOptions() {
    var opts = {};
    var selects = document.querySelectorAll('.dpo-container select, [class*="itoris-dpo"] select');
    for (var i = 0; i < selects.length; i++) {
      var s = selects[i];
      if (s.offsetParent === null || !isDpoField(s)) continue;
      var sel = s.options[s.selectedIndex];
      var val = sel ? stripSurcharge(sel.text || sel.value || '') : '';
      if (val && !/^(please select|select|---)$/i.test(val)) opts[dpoLabelFor(s)] = val.slice(0, 80);
    }
    var radios = document.querySelectorAll('.dpo-container input[type="radio"]:checked, [class*="itoris-dpo"] input[type="radio"]:checked');
    for (var j = 0; j < radios.length; j++) {
      var r = radios[j];
      if (!isDpoField(r)) continue;
      var lab = dpoLabelFor(r);
      if (lab.toLowerCase().indexOf('shape') !== -1) continue; // shape reported separately
      opts[lab] = radioValueLabel(r);
    }
    return opts;
  }

  // Passive price read (v3 — verified live 2026-07-12): the dev theme renders
  // MANY identically-classed visible `.money` nodes (related products etc. all
  // £0.00); the DPO-driven one lives in the same section as .dpo-container and
  // is the only non-zero one. So: prefer a dedicated DPO node; else scan price
  // nodes scoped to the DPO product section, preferring the first NON-ZERO.
  function parseMoney(el) {
    var t = el && el.textContent ? el.textContent : '';
    var m = t.replace(/,/g, '').match(/(\d+(?:\.\d{1,2})?)/);
    return m ? parseFloat(m[1]) : null;
  }
  function priceNode() {
    var dpo = document.querySelector('.dpo_price_placeholder, [class*="dpo_price"], [class*="dpo-price"]');
    if (dpo) return dpo;
    var dpoC = dpoContainer();
    var scope = dpoC
      ? (dpoC.closest('section') || dpoC.closest('form') || dpoC.closest('main') || document)
      : document;
    var nodes = scope.querySelectorAll('.money, [data-product-price], [class*="price-item"]');
    var firstVisible = null;
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (el.offsetParent === null || el.querySelector('.money')) continue;
      var v = parseMoney(el);
      if (v === null) continue;
      if (firstVisible === null) firstVisible = el;
      if (v > 0) return el; // the DPO-calculated price
    }
    return firstVisible;
  }
  function readPrice() {
    return parseMoney(priceNode());
  }
  function readCurrency() {
    try {
      if (window.Currency && Currency.currentCurrency) return Currency.currentCurrency;
      if (window.Shopify && Shopify.currency && Shopify.currency.active) return Shopify.currency.active;
    } catch (e) { /* fall through */ }
    var el = priceNode();
    var t = el && el.textContent ? el.textContent : '';
    if (t.indexOf('£') !== -1) return 'GBP';
    if (t.indexOf('€') !== -1) return 'EUR';
    if (t.indexOf('$') !== -1) return 'USD';
    return window.shopCurrency || 'GBP';
  }

  // --- implausible dims (TR-35): generic unit-confusion detector
  var implausibleFlagged = {};
  function normaliseUnit(u) {
    var t = String(u || '').toLowerCase();
    if (t.indexOf('cent') === 0 || t === 'cm') return 'cm';
    if (t.indexOf('met') === 0 || t === 'm') return 'm';
    if (t.indexOf('mil') === 0 || t === 'mm') return 'mm';
    if (t.indexOf('inch') === 0 || t === 'in') return 'in';
    if (t.indexOf('f') === 0) return 'ft';
    return null;
  }
  function checkPlausibility(field, rawValue) {
    var cfg = readDpoConfig();
    var unitLabel = null;
    if (cfg) {
      Object.keys(cfg).forEach(function (k) { if (k.toLowerCase().indexOf('unit') !== -1) unitLabel = cfg[k]; });
    }
    var unit = normaliseUnit(unitLabel) || normaliseUnit(readUnits()) || 'cm';
    var range = DPO.plausible[unit];
    if (!range) return;
    var v = parseFloat(String(rawValue).replace(/,/g, ''));
    if (!isFinite(v) || v === 0) return;
    if ((v < range[0] || v > range[1]) && !implausibleFlagged[field + ':' + v]) {
      implausibleFlagged[field + ':' + v] = true;
      track('implausible_dims_flag', { field: field, value: v, unit: unit });
    }
  }

  // --- quote state
  var quoteCount = 0;
  var lastQuoteKey = null;
  var lastQuotePrice = null;
  var quoteTimer = null;

  // Prefer DPO's own field map (label -> value, e.g. {"Length A":"1",
  //   "Fabric type":"NylonSafe", "Unit of measure":"Metres"}) — the same call
  // the theme's free-design button uses. DOM scraping stays as fallback.
  function readDpoConfig() {
    try {
      if (window.dpoOptions && typeof dpoOptions.createShopifyCartProperties === 'function') {
        var cfg = dpoOptions.createShopifyCartProperties(true);
        if (cfg && typeof cfg === 'object' && Object.keys(cfg).length > 0) return cfg;
      }
    } catch (e) { /* fall through to DOM reads */ }
    return null;
  }

  function buildQuote() {
    var price = readPrice();
    if (price === null || price <= 0) return null;
    var cfg = readDpoConfig();
    if (cfg) {
      var measurements = {};
      var options = {};
      var units = null;
      var shape = null;
      Object.keys(cfg).forEach(function (k) {
        var v = String(cfg[k]);
        var kl = k.toLowerCase();
        if (kl.indexOf('unit') !== -1) units = v;
        else if (kl.indexOf('shape') !== -1) shape = v;
        else if (/^-?\d+(\.\d+)?$/.test(v.trim())) measurements[k] = v.trim();
        else options[k] = v;
      });
      if (Object.keys(measurements).length === 0) return null; // no dims yet
      return {
        measurements: measurements,
        units: units,
        price: price,
        currency: readCurrency(),
        shape: shape,
        options: options,
        product: PRODUCT_ID,
        product_name: PRODUCT_TITLE,   // MUST stay 'product_name' — the Klaviyo R1 subject reads event.product_name
        product_url: PRODUCT_URL,
        product_image: PRODUCT_IMAGE,
        config_ordered: readOrderedConfig()
      };
    }
    if (!allMeasurementsFilled()) return null;
    return {
      measurements: readMeasurements(),
      units: readUnits(),
      price: price,
      currency: readCurrency(),
      shape: readShape(),
      options: readOptions(),
      product: PRODUCT_ID,
      product_name: PRODUCT_TITLE,   // MUST stay 'product_name' — the Klaviyo R1 subject reads event.product_name
      product_url: PRODUCT_URL,
      product_image: PRODUCT_IMAGE,
      config_ordered: readOrderedConfig()
    };
  }

  function maybeQuote() {
    var q = buildQuote();
    if (!q) return;
    var key;
    try { key = JSON.stringify([q.measurements, q.shape, q.options, q.price]); }
    catch (e) { key = String(q.price); }
    if (key === lastQuoteKey) return; // identical recalc — not a new quote
    var prevPrice = lastQuotePrice;
    lastQuoteKey = key;
    lastQuotePrice = q.price;
    quoteCount++;
    Object.keys(q.measurements).forEach(function (f) { checkPlausibility(f, q.measurements[f]); });
    track('quote_generated', q);
    claritySet('quote_generated', 'true'); // TR-06 replay filter tag
    armQuoteRecovery(); // TR-37 — prompt only ever arms POST-quote
    mountInlineQuoteCta(); // TR-61 — persistent pull surface, no cap
    stampCart(); // pre-ATC stamp — DPO's programmatic ATC fires no submit event
    if (quoteCount > 1 && prevPrice !== null && prevPrice !== q.price) {
      track('quote_reconfigured', { n: quoteCount, old_price: prevPrice, new_price: q.price });
    }
  }

  function scheduleQuote() {
    if (quoteTimer) clearTimeout(quoteTimer);
    // debounce 1.5s so mid-typing recalcs don't spam (spec §2.4); dispatch via
    // idle callback where available — capture must never add jank to DPO's
    // already-heavy recalc (DPO-HOOKS behaviour fact 3).
    quoteTimer = setTimeout(function () {
      quoteTimer = null;
      if (window.requestIdleCallback) requestIdleCallback(maybeQuote, { timeout: 2000 });
      else maybeQuote();
    }, DPO.quoteDebounceMs);
  }

  // --- configurator interaction events (delegated — fields render late)
  var measurementStarted = {};
  var lastFieldValues = {};
  var unitBefore = null;

  function isUnitField(el) {
    return dpoLabelFor(el).toLowerCase().indexOf('unit') !== -1;
  }

  document.addEventListener('focusin', function (ev) {
    var el = ev.target;
    if (!isMeasurementInput(el) || !trackingActive()) return;
    var f = dpoLabelFor(el);
    if (!measurementStarted[f]) {
      measurementStarted[f] = true;
      track('measurement_started', { field: f });
    }
    if (isUnitField(el)) unitBefore = el.value;
  }, true);

  document.addEventListener('change', function (ev) {
    var el = ev.target;
    if (!el || !trackingActive()) return;

    if (isMeasurementInput(el)) {
      var f = dpoLabelFor(el);
      var v = (el.value || '').trim();
      if (v) {
        track('measurement_completed', { field: f });
        checkPlausibility(f, v);
      }
      return;
    }

    if (el.tagName === 'SELECT' && isDpoField(el)) {
      var label = dpoFieldLabel(el, (el.options[el.selectedIndex] || {}).text);
      var selOpt = el.options[el.selectedIndex];
      var to = selOpt ? (selOpt.text || selOpt.value || '').trim().slice(0, 80) : '';
      var from = lastFieldValues[label] || null;
      lastFieldValues[label] = to;
      if (isUnitField(el)) {
        var lost = Object.keys(readMeasurements()).length;
        track('unit_changed', { from: from, to: to, fields_lost: lost });
      } else {
        track('option_changed', { field: label, from: from, to: to });
      }
      return;
    }

    if (el.type === 'radio' && isDpoField(el)) {
      var rVal = radioValueLabel(el);
      var rLabel = dpoFieldLabel(el, rVal);
      var rFrom = lastFieldValues[rLabel] || null;
      lastFieldValues[rLabel] = rVal;
      if (rLabel.toLowerCase().indexOf('shape') !== -1) {
        track('shape_selected', { shape: rVal });
      } else if (isUnitField(el)) {
        var lost2 = Object.keys(readMeasurements()).length;
        track('unit_changed', { from: rFrom, to: rVal, fields_lost: lost2 });
      } else {
        track('option_changed', { field: rLabel, from: rFrom, to: rVal });
      }
      return;
    }

    if (el.type === 'file' && (el.className || '').indexOf('product-custom-option') !== -1) {
      track('file_uploaded', { count: el.files ? el.files.length : 1 });
      return;
    }

    if (el.tagName === 'TEXTAREA' && isDpoField(el)) {
      var len = (el.value || '').trim().length;
      if (len > 0) track('notes_entered', { length: len });
    }
  }, true);

  // --- validation errors (TR-33): DPO injects advice/error nodes into rows
  var validationCount = 0;
  function watchValidation(container) {
    if (!window.MutationObserver) return;
    new MutationObserver(function (muts) {
      if (validationCount >= DPO.maxValidationEvents || !trackingActive()) return;
      for (var i = 0; i < muts.length; i++) {
        var added = muts[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var n = added[j];
          if (n.nodeType !== 1) continue;
          var cls = String(n.className || '');
          if (/advice|validation|error/i.test(cls) && n.textContent && n.textContent.trim()) {
            validationCount++;
            track('validation_error', {
              field: dpoLabelFor(n),
              reason: n.textContent.replace(/\s+/g, ' ').trim().slice(0, 120)
            });
            if (validationCount >= DPO.maxValidationEvents) return;
          }
        }
      }
    }).observe(container, { childList: true, subtree: true });
  }

  // --- hook the DPO event bus (primary) or the price node (fallback)
  var dpoHooked = false;
  function hookDpo() {
    if (dpoHooked) return true;
    try {
      if (window.dpoEvent && typeof dpoEvent.observe === 'function') {
        // jQuery-triggered on window — the ONLY visible target (AD0 2026-07-12)
        dpoEvent.observe(window, 'dpoPriceUpdate', scheduleQuote);
        dpoEvent.observe(window, 'dpo_uploaded_file', function () {
          track('file_uploaded', { count: 1 });
        });
        // DPO ATC is a programmatic temp-form submit (no submit event) —
        // its own event bus is the only reliable signal.
        dpoEvent.observe(window, 'dpo_before_form_submit', function () {
          stampCart();
        });
        dpoEvent.observe(window, 'dpo_add2cart', function () {
          stampCart();
          track('add_to_cart', { product_id: PRODUCT_ID, via: 'dpo' });
          flush(true); // page may navigate to cart imminently
        });
        dpoHooked = true;
        return true;
      }
    } catch (e) { /* fall through to fallback */ }
    return false;
  }

  function hookPriceFallback() {
    var el = priceNode();
    if (!el || !window.MutationObserver) return false;
    new MutationObserver(scheduleQuote)
      .observe(el, { childList: true, subtree: true, characterData: true });
    dpoHooked = true;
    return true;
  }

  // A resumed page (?lead=&lt=) is repriced by the resume snippet replaying
  // values, which does not always surface as a dpoPriceUpdate the tracker sees —
  // so the quote existed but the inline control never mounted, exactly where a
  // returning customer most needs it. Poll briefly after load: maybeQuote() is
  // idempotent (it dedups on the quote key), so this is cheap and safe.
  function pollForQuote() {
    var tries = 0;
    var iv = setInterval(function () {
      tries++;
      try { maybeQuote(); } catch (e) {}
      if (tries >= 20 || document.getElementById('kuvr-inline-quote')) clearInterval(iv);
    }, 1500);
  }

  function initDpoCapture() {
    var polls = 0;
    var iv = setInterval(function () {
      polls++;
      var container = dpoContainer();
      if (hookDpo()) {
        clearInterval(iv);
        if (container) watchValidation(container);
        pollForQuote();
        return;
      }
      if (polls >= DPO.maxPolls) {
        clearInterval(iv);
        pollForQuote();
        // event bus never appeared — PROVEN fallback: observe the price node
        if (hookPriceFallback() && container) watchValidation(container);
      }
    }, DPO.pollMs);
  }

  // ------------------------------------------- free-design leads (Sprint 2, TR-08)
  // The theme's design-request modal form posts to a Make.com webhook (traced
  // Sprint 2 — snippets/product-form-calculator.liquid #designRequestForm).
  // We listen passively in the capture phase: never preventDefault, never
  // alter the existing flow. Lead creation itself is operational (essential/
  // pre-contract basis) and runs regardless of behavioural consent; the
  // free_design_submitted EVENT stays consent-gated via track().
  var lastLeadSubmitAt = 0;

  document.addEventListener('click', function (ev) {
    var t = ev.target && ev.target.closest &&
      (ev.target.closest('#request-free-design') || ev.target.closest('#show-new-design-cart-button'));
    if (t) track('free_design_started', { via: t.id });
  }, true);

  // Lead type → taxonomy event fired on submission (TR-36: three typed paths).
  var LEAD_SUBMIT_EVENT = {
    design: 'free_design_submitted',
    expert: 'expert_contact_submitted',
    recovery: 'quote_recovery_submitted'
  };

  /**
   * Link this browser to a Klaviyo profile (TR-71).
   *
   * Klaviyo SILENTLY DISCARDS a client event whose cookie carries no $email —
   * POST /client/events/ returns 202 either way and isIdentified() returns true
   * off an $exchange_id alone. Verified 2026-08-20. Without this call the
   * `Viewed Product` events pushed by snippets/kuvr-klaviyo-viewed.liquid never
   * record, and welcome email W3 has no browse history to personalise from.
   *
   * We learn the email here — at quote capture — earlier than Klaviyo would
   * (it only identifies after someone clicks a Klaviyo email link).
   *
   * ⚠️ This has been overwritten once already by a parallel edit to this file.
   * If it disappears, Viewed Product silently stops recording. See TR-71.
   */
  function identifyToKlaviyo(email) {
    if (!email || IS_INTERNAL) return;
    try {
      var k = window.klaviyo;
      if (k && typeof k.identify === 'function') {
        k.identify({ '$email': email });
      } else {
        (window._learnq = window._learnq || []).push(['identify', { '$email': email }]);
      }
    } catch (e) { /* identification must never break the submission */ }
  }

  // `onResult` (optional) is called once the API has answered, with
  //   { ok, leadId, deduped, reason }
  // so a caller can tell the customer the truth instead of promising mail.
  // 🛑 The API returns reason:'duplicate_within_window' when it suppresses a
  // repeat of an identical quote spec inside 10 minutes. Until 2026-08-21 nothing
  // read it and both surfaces said "Sent!" regardless — so the one case the
  // response was added to describe was still being lied about. It fires on the
  // error path too, so a caller never waits forever for a callback.
  function sendLead(fields, onResult) {
    function report(res) {
      if (typeof onResult !== 'function') return;
      try { onResult(res); } catch (e) { /* UI must never break capture */ }
    }
    var now2 = Date.now();
    if (now2 - lastLeadSubmitAt < 2000) { report({ ok: false, throttled: true }); return; }
    lastLeadSubmitAt = now2;
    if (!fields.email) { report({ ok: false }); return; }
    identifyToKlaviyo(fields.email);
    var leadType = LEAD_SUBMIT_EVENT[fields.type] ? fields.type : 'design';
    var submitEvent = LEAD_SUBMIT_EVENT[leadType];

    var payload = {
      visitor_id: visitorId,
      session_id: sessionId,
      type: leadType,
      email: fields.email,
      name: fields.name || undefined,
      phone: fields.phone || undefined,
      product: fields.product || PRODUCT_ID || undefined,
      // Emailed as "£172.6" because this was the raw number as a string. Klaviyo
      // renders quote_price verbatim when quote_price_display is absent, so the
      // formatting has to be right here. parseFloat still yields 172.6 downstream,
      // so the Klaviyo event `value` (used for revenue attribution) is unaffected.
      quote_price: fields.quote_price !== undefined ? fields.quote_price
        : (lastQuotePrice !== null ? Number(lastQuotePrice).toFixed(2) : undefined),
      quote_config: fields.quote_config || (function () {
        // ORDER MATTERS: this object is passed straight through by Nexus
        // (/api/track/lead -> klaviyo properties.quote_config) and rendered as the
        // quote summary. It used to come back scrambled — Fabric type, Length B,
        // Shape, Material colour, Fasteners, Fixing type, Length C, Unit, Length A —
        // because readDpoConfig/readOptions group by INPUT TYPE (all selects, then
        // all radios) rather than by position, so A/B/C were split apart.
        // readOrderedConfig walks the DOM once in true visual order; rebuilding the
        // map from it means JSON insertion order matches what the customer saw.
        var ordered = readOrderedConfig();
        // The `window.__kuvrQuoteDebug` diagnostic that lived here has been removed
        // (2026-08-20, its job done). It logged what this function produced on every
        // quote, and it is what finally proved the theme was emitting the correct
        // order all along — the fault was in the Klaviyo template. Worth re-adding
        // temporarily if the option order is ever in doubt again: measure first.
        if (ordered && ordered.length) {
          // 🛑 SEND AN ARRAY, NOT A MAP. Proven 2026-08-20: this function emits the
          // CORRECT order (console diagnostic showed orderedLabels ascending and
          // usedFallback:false) yet the email still rendered it scrambled — because a
          // map's key order is not preserved across JSON transport and Klaviyo's event
          // property storage. Ordering a structure whose order is discarded downstream
          // is why six theme-side fixes all failed.
          // The template's own header documents the contract as
          //   CONFIGURATION (list of {label,value})
          // i.e. it always wanted a LIST. Arrays preserve order everywhere.
          //
          // Repair labels while we are here: dpoFieldLabel reverse-looks-up the field
          // name by matching the value in DPO's own config, which fails for priced
          // add-ons ("2 strips+£25.00" came through as its own label). When DPO's key
          // list lines up 1:1 with ours, take the name from it by position.
          // NOTE: an earlier attempt repaired labels by taking DPO's config keys BY
          // POSITION. That was unsound — DPO's key order is not our visual order, so it
          // renamed fields to each other's names: a quote showed "Fasteners: Zip split"
          // where the product calls that field "Splits", and the resume link then could
          // not find the field and left it blank. Never guess a label by position.
          // The label now comes from the product's own DOM (see fieldLabelFor), so it is
          // correct on every product without knowing anything about that product.
          return ordered.slice();
        }
        var cfg = readDpoConfig();
        if (cfg) return cfg; // flat label->value map from DPO itself
        if (!lastQuoteKey) return undefined;
        return {
          measurements: readMeasurements(),
          units: readUnits(),
          shape: readShape(),
          options: readOptions()
        };
      })(),
      consent: {
        status: consent.status,
        basis: consent.basis,
        timestamp: consent.timestamp || undefined,
        version: consent.version
      },
      internal: !!IS_INTERNAL,
      source: fields.source || undefined,
      hp: ''
    };

    try {
      fetch(CONFIG.leadEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
        credentials: 'omit'
      }).then(function (r) { return r.json(); }).then(function (j) {
        var leadId = (j && j.lead_id) || null;
        track(submitEvent, { lead_id: leadId });
        report({
          ok: true,
          leadId: leadId,
          deduped: !!(j && j.deduped),
          reason: (j && j.reason) || null
        });
        // TR-19: browser-side Meta Lead pixel, event_id matches the server
        // CAPI convention in ad-uploads.ts (metaEventId.lead) so Meta dedups
        // the two into one event. Internal/staff submissions are excluded
        // from ad tracking everywhere else, so exclude them here too.
        if (leadId && !IS_INTERNAL && typeof window.fbq === 'function') {
          try {
            window.fbq('track', 'Lead', {}, { eventID: 'lead_' + leadId });
          } catch (e3) { /* pixel failure must never break the real submission */ }
        }
        flush(false);
      }).catch(function () {
        track(submitEvent, { lead_id: null });
        report({ ok: false });
        flush(false);
      });
    } catch (e) {
      track(submitEvent, { lead_id: null });
      report({ ok: false });
    }
  }

  // 🛑 CAPTURE THE OPTION ORDER WHEN THE ITEM IS ADDED, NOT WHEN IT IS SUBMITTED.
  //
  // The design cart stores `dpoData` = dpoOptions.createShopifyCartProperties(),
  // which is a MAP in DPO's internal key order — the same scrambled order that
  // cost six failed attempts on the quote email (see the theme's
  // SESSION-2026-08-20 notes §3). Draft order #D998 shows it plainly: "Material
  // colour, Shape, Unit, Length A" where the product asks for unit first.
  //
  // readOrderedConfig() reads the live product DOM in true visual order, so it
  // has to run on the PRODUCT page at the moment of adding — by the time the
  // wizard's step 3 submits, the customer may have moved on to another product,
  // and a multi-item cart has no single page to read. Stamping the item as it
  // goes in is the only point where the right DOM is guaranteed to be present.
  //
  // Pass-through and defensive: if anything here throws, the item is still added
  // exactly as before, just without the ordered copy, and the submit path falls
  // back to dpoData.
  (function wrapDesignAdd() {
    var tries = 0;
    var iv = setInterval(function () {
      tries++;
      var orig = window.addToDesignCart;
      if (typeof orig === 'function' && !orig._kuvrWrapped) {
        var wrapped = function (designRequest) {
          try {
            if (designRequest && typeof designRequest === 'object') {
              var ordered = readOrderedConfig();
              if (ordered && ordered.length) designRequest.kuvrOrderedConfig = ordered;
            }
          } catch (e) { /* never block an add to the design cart */ }
          return orig.apply(this, arguments);
        };
        wrapped._kuvrWrapped = true;
        window.addToDesignCart = wrapped;
        clearInterval(iv);
      }
      if (tries >= 40) clearInterval(iv); // 20s — page may not have the wizard
    }, 500);
  })();

  // PRIMARY path (theme's restored design-cart wizard, verified 2026-07-12):
  // the step-3 button calls global submitNewDesignRequest() (no form submit
  // event fires). Wrap it pass-through once it exists; onclick resolves the
  // global at call time, so the wrapper is picked up automatically.
  (function wrapDesignSubmit() {
    var tries = 0;
    var iv = setInterval(function () {
      tries++;
      var orig = window.submitNewDesignRequest;
      if (typeof orig === 'function' && !orig._kuvrWrapped) {
        var wrapped = function () {
          try {
            var form = document.getElementById('new-design-contact-form');
            var fd = form ? new FormData(form) : null;
            var first = fd ? String(fd.get('firstName') || '').trim() : '';
            var last = fd ? String(fd.get('lastName') || '').trim() : '';
            var codeEl = document.getElementById('new-design-selected-code');
            var phoneNum = fd ? String(fd.get('phone') || '').trim() : '';
            var cart = [];
            try { cart = JSON.parse(localStorage.getItem('designCart') || '[]'); } catch (e2) {}
            // 🛑 addToDesignCart UNSHIFTS — the newest item is cart[0], not the last.
            // This read cart[cart.length - 1] and so described the FIRST product added
            // whenever the customer requested designs for more than one thing.
            var item = cart.length ? cart[0] : null;

            // Ordered list stamped at add-time; the raw dpoData map is the fallback
            // for items added before this shipped, or if that capture threw.
            var cfg = null;
            if (item) {
              if (item.kuvrOrderedConfig && item.kuvrOrderedConfig.length) {
                cfg = item.kuvrOrderedConfig.slice();
              } else if (item.dpoData && typeof item.dpoData === 'object') {
                cfg = item.dpoData;
              }
            }
            // A lead carries ONE product, but the wizard submits the whole cart. Say so,
            // rather than let the alert imply a one-product request. Only possible on the
            // ordered shape; the legacy map is left untouched.
            if (cfg && Object.prototype.toString.call(cfg) === '[object Array]' && cart.length > 1) {
              cfg.push({
                label: 'Other items in this request',
                value: cart.slice(1).map(function (it) {
                  return (it && (it.productTitle || it.productHandle)) || 'unnamed item';
                }).join(', ')
              });
            }

            sendLead({
              email: fd ? String(fd.get('email') || '').trim() : '',
              name: (first + ' ' + last).trim(),
              phone: phoneNum ? ((codeEl && codeEl.textContent ? codeEl.textContent.trim() + ' ' : '') + phoneNum) : undefined,
              product: item ? item.productHandle : undefined,
              quote_price: item && item.dpoPrice ? String(item.dpoPrice) : undefined,
              quote_config: cfg || undefined
            });
          } catch (e) { /* capture must never break the real submission */ }
          return orig.apply(this, arguments);
        };
        wrapped._kuvrWrapped = true;
        window.submitNewDesignRequest = wrapped;
        clearInterval(iv);
      }
      if (tries >= 40) clearInterval(iv); // 20s — page may not have the wizard
    }, 500);
  })();

  // LEGACY path (old #designRequestForm modal — kept in the theme snippets)
  document.addEventListener('submit', function (ev) {
    var form = ev.target;
    if (!form || form.id !== 'designRequestForm') return;
    var email = '';
    var name = '';
    var handle = null;
    try {
      var fd = new FormData(form);
      email = String(fd.get('customer_email') || '').trim();
      name = String(fd.get('customer_name') || '').trim();
      handle = String(fd.get('product_handle') || '').trim() || null;
    } catch (e) { /* FormData unsupported — skip lead */ }
    sendLead({ email: email, name: name, product: handle });
  }, true);

  // ------------------------------------------- quote recovery (TR-37, §5.6)
  // "Email me my quote & design" for high-intent sessions going idle
  // post-quote (or exit-intent on desktop). Opt-in, frequency-capped, NEVER
  // shown pre-quote. Creates a `recovery` lead carrying the full quote
  // config → dedicated Klaviyo flow.
  var recoveryArmed = false;
  var recoveryShown = false;
  var recoveryIdleTimer = null;

  // ------------------------------------------------- TR-61 inline quote CTA
  // A persistent pull surface on the price row. Deliberately NOT capped: the
  // pop-up card is a push and needs a cap, this is something the customer goes
  // looking for. It also gives mobile parity — exit-intent cannot fire on touch,
  // so without this the card's only mobile trigger is 45s of true inactivity.
  var inlineCtaMounted = false;

  // Theme teal, sampled from the live price and the theme's own "Learn more"
  // links (rgb(0,111,108)) — not invented. Keep these in step with the theme.
  var KUVR_TEAL = '#006F6C';

  function inlinePriceHost() {
    var dpo = document.getElementById('itoris_dynamicproductoptions');
    var scope = dpo && dpo.closest ? dpo.closest('.productView-product') : null;
    if (!scope) return null;
    // The DIRECT PARENT of the money span — span.price-item--regular — so the
    // control is a genuine inline sibling of the number. Attaching to the <dd>
    // above it made the flex baseline resolve against that wrapper's 55px line
    // box instead of the digits, which is where the constant 6.5px error came
    // from. No flex here: plain inline layout puts them on one baseline.
    var monies = scope.querySelectorAll('.price__regular .money');
    for (var i = 0; i < monies.length; i++) {
      if (monies[i].offsetParent === null) continue;
      return monies[i].parentElement;
    }
    return scope.querySelector('.atc-price-wrap') || null;
  }

  // Zero-height inline-block sits exactly on the baseline of its line box, so
  // appending one gives an element's true text baseline.
  function textBaseline(el) {
    if (!el) return null;
    var strut = document.createElement('span');
    strut.style.cssText = 'display:inline-block;width:0;height:0;overflow:hidden;padding:0;margin:0;border:0;';
    el.appendChild(strut);
    var b = strut.getBoundingClientRect().bottom;
    el.removeChild(strut);
    return b;
  }

  // The label and the price are inline-block siblings, both vertical-align
  // baseline, and STILL measured a constant 6.5px apart — something in the
  // theme wins the cascade. Rather than keep hunting it, compensate by the
  // measured amount. Always measured from a RESET state with a forced reflow,
  // so it computes the same value every time instead of feeding back on itself.
  function alignToPriceBaseline() {
    var box = document.getElementById('kuvr-inline-quote');
    if (!box) return;
    var label = box.querySelector('a span');
    var input = box.querySelector('#kuvr-iq-email');
    if (!label && !input) return;
    var dpo = document.getElementById('itoris_dynamicproductoptions');
    var scope = dpo && dpo.closest ? dpo.closest('.productView-product') : null;
    if (!scope) return;
    var money = null, monies = scope.querySelectorAll('.price__regular .money');
    for (var i = 0; i < monies.length; i++) {
      if (monies[i].offsetParent !== null) { money = monies[i]; break; }
    }
    if (!money) return;
    try {
      box.style.setProperty('position', 'relative', 'important');
      box.style.setProperty('top', '0px', 'important');
      box.style.setProperty('margin-left', '18px', 'important');
      void box.offsetHeight;                  // force layout before measuring

      // On a narrow screen the control wraps under the price. The 18px gap that
      // separates it from the number on one line then reads as a stray indent,
      // leaving it floating out of line with everything else in the column. When
      // it wraps: no indent, and no baseline nudge — there is no baseline beside
      // it to match any more.
      var wrapped = box.getBoundingClientRect().top > money.getBoundingClientRect().bottom - 10;
      if (wrapped) {
        box.style.setProperty('margin-left', '0px', 'important');
        box.style.setProperty('top', '0px', 'important');
        return;
      }
      // An <input> cannot take a strut, so derive its text baseline from font
      // metrics: content box, text vertically centred in it, plus the ascent.
      var inputBaseline = function (inp) {
        var cs = window.getComputedStyle(inp);
        var r = inp.getBoundingClientRect();
        var bt = parseFloat(cs.borderTopWidth) || 0, pt = parseFloat(cs.paddingTop) || 0;
        var pb = parseFloat(cs.paddingBottom) || 0, bb = parseFloat(cs.borderBottomWidth) || 0;
        var contentTop = r.top + bt + pt;
        var contentH = r.height - bt - pt - pb - bb;
        var ctx = document.createElement('canvas').getContext('2d');
        ctx.font = cs.fontStyle + ' ' + cs.fontWeight + ' ' + cs.fontSize + ' ' + cs.fontFamily;
        var m = ctx.measureText('Exy');
        var asc = m.actualBoundingBoxAscent, desc = m.actualBoundingBoxDescent;
        if (!isFinite(asc) || !isFinite(desc)) { asc = parseFloat(cs.fontSize) * 0.72; desc = parseFloat(cs.fontSize) * 0.22; }
        return contentTop + (contentH - (asc + desc)) / 2 + asc;
      };
      // For the LINK, match text baseline to text baseline.
      // For the INPUT, sit the bottom edge of the field on the price's baseline.
      // Modelling where a browser paints text inside an <input> was the mistake:
      // the model agreed with itself to 0.1px while the field visibly hung below
      // the digits. The box edge is not a model — it is measurable.
      var delta = label ? (textBaseline(money) - textBaseline(label))
                        : (textBaseline(money) - input.getBoundingClientRect().bottom);
      if (isFinite(delta) && Math.abs(delta) > 0.3 && Math.abs(delta) < 40) {
        box.style.setProperty('top', delta.toFixed(2) + 'px', 'important');
      }
    } catch (e) {}
  }

  // Restored: this was deleted by accident during a refactor, and because it is
  // called from the click handler its absence threw a ReferenceError *before*
  // the input and button were styled — which is why the form rendered huge and
  // unstyled. The control sits inside the price block (50px bold teal), so it
  // inherits weight and colour as well as size, and ::placeholder cannot be
  // reached from an inline style at all. Hence a real stylesheet.
  var kuvrIqStyled = false;
  function ensureInlineQuoteStyles() {
    if (kuvrIqStyled || document.getElementById('kuvr-iq-style')) return;
    kuvrIqStyled = true;
    var st = document.createElement('style');
    st.id = 'kuvr-iq-style';
    st.textContent =
      '#kuvr-inline-quote{font-size:15px!important;font-weight:400!important;' +
      'line-height:1.2!important;letter-spacing:normal!important;}' +
      '#kuvr-iq-form{display:flex!important;align-items:center!important;gap:8px!important;}' +
      '#kuvr-iq-email{font-weight:400!important;color:#202223!important;' +
      'font-family:inherit!important;font-size:14px!important;line-height:1.2!important;' +
      'letter-spacing:normal!important;text-transform:none!important;width:190px!important;' +
      'height:auto!important;padding:8px 10px!important;border:1px solid #c9c9c9!important;' +
      'border-radius:4px!important;box-shadow:none!important;background:#fff!important;}' +
      '#kuvr-iq-email::placeholder{font-weight:400!important;color:#8a8f94!important;' +
      'opacity:1!important;letter-spacing:normal!important;text-transform:none!important;' +
      'font-size:14px!important;}' +
      '#kuvr-iq-send{font-weight:400!important;letter-spacing:normal!important;' +
      'text-transform:none!important;font-size:14px!important;line-height:1.2!important;' +
      'padding:9px 14px!important;border:0!important;border-radius:4px!important;' +
      'background:#006F6C!important;color:#fff!important;height:auto!important;' +
      'width:auto!important;box-shadow:none!important;}';
    (document.head || document.documentElement).appendChild(st);
  }

  function setImp(el, props) {
    Object.keys(props).forEach(function (k) {
      try { el.style.setProperty(k, props[k], 'important'); } catch (e) {}
    });
  }

  function mountInlineQuoteCta() {
    if (inlineCtaMounted) return;
    if (!lastQuoteKey) return; // never pre-quote — there is nothing to email
    var host = inlinePriceHost();
    if (!host || document.getElementById('kuvr-inline-quote')) return;
    inlineCtaMounted = true;

    var box = document.createElement('span');
    box.id = 'kuvr-inline-quote';
    // display:block, NOT inline-flex. A flex box takes its baseline from its
    // FIRST FLEX ITEM — here the SVG envelope, which has no text baseline — so
    // the label sat 6.5px high no matter what align-items said. As a plain block
    // of inline text the baseline is the text's own, which is what must line up
    // with the price.
    setImp(box, {
      display: 'inline-block', 'vertical-align': 'baseline', 'margin-left': '18px',
      'font-size': '15px', 'line-height': '1.2', 'font-weight': '400',
      'letter-spacing': 'normal', 'white-space': 'nowrap',
      position: 'static', top: 'auto'
    });

    var link = document.createElement('a');
    link.id = 'kuvr-iq-open';
    link.href = '#';
    link.innerHTML =
      '<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
      'stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" ' +
      'style="width:17px;height:17px;flex:0 0 auto;display:block;">' +
      '<rect x="3" y="5" width="18" height="14" rx="2"></rect>' +
      '<path d="m3 7.5 9 6 9-6"></path></svg><span>Email quote</span>';
    // display:inline (not inline-flex) on purpose. An inline-flex box takes its
    // baseline from its first flex item — the SVG — which has no text baseline,
    // so the label floated above the price's baseline. As plain inline text the
    // anchor uses its own text baseline and lines up with the number.
    setImp(link, {
      display: 'inline', color: KUVR_TEAL, 'font-family': 'inherit',
      'font-size': '15px', 'font-weight': '400', 'line-height': '1.2',
      'text-decoration': 'none', 'letter-spacing': 'normal',
      cursor: 'pointer', 'white-space': 'nowrap'
    });
    var span = link.querySelector('span');
    if (span) setImp(span, { 'font-size': '15px', 'font-weight': '400', 'line-height': '1.2' });
    // The theme sets `fill: currentColor` on svg, which turns an outline icon
    // into a solid block. Force the stroke-only rendering back on.
    var svgEl = link.querySelector('svg');
    if (svgEl) {
      setImp(svgEl, {
        fill: 'none', stroke: KUVR_TEAL, width: '16px', height: '16px',
        display: 'inline-block', 'vertical-align': '-2px', 'margin-right': '7px'
      });
      var kids = svgEl.querySelectorAll('*');
      for (var si = 0; si < kids.length; si++) {
        setImp(kids[si], { fill: 'none', stroke: KUVR_TEAL });
      }
    }
    box.appendChild(link);
    host.appendChild(box);
    alignToPriceBaseline();
    // Fonts can land after first paint and shift the baseline.
    setTimeout(alignToPriceBaseline, 400);
    setTimeout(alignToPriceBaseline, 1500);
    if (document.fonts && document.fonts.ready && document.fonts.ready.then) {
      document.fonts.ready.then(alignToPriceBaseline).catch(function () {});
    }
    var alignTimer = null;
    window.addEventListener('resize', function () {
      if (alignTimer) clearTimeout(alignTimer);
      alignTimer = setTimeout(alignToPriceBaseline, 150);
    }, { passive: true });
    track('quote_email_surface_shown', { source: 'inline' });

    link.addEventListener('click', function (ev) {
      ev.preventDefault();
      track('quote_email_clicked', { source: 'inline' });
      // TR-73: one modal for both entry points — collects a first name and
      // validates the address, neither of which the inline field could do.
      // Falls through to the original inline form if the modal cannot build,
      // so capture never depends on it.
      if (openQuoteCaptureModal('inline', function () {
        box.innerHTML = '';
        var ok = document.createElement('span');
        ok.textContent = 'Sent — check your inbox.';
        setImp(ok, { 'font-family': 'inherit', 'font-size': '15px', color: KUVR_TEAL, 'line-height': '1.2' });
        box.appendChild(ok);
      })) return;
      box.innerHTML = '';
      var form = document.createElement('form');
      form.id = 'kuvr-iq-form';
      // NOT flex: a flex container has no line box, so a baseline strut inside it
      // measures the box edge, not the text — which is why the form sat off the
      // price baseline while the link had been fine.
      setImp(form, { display: 'flex', 'align-items': 'center', gap: '8px' });
      form.innerHTML =
        '<label for="kuvr-iq-email" style="position:absolute;width:1px;height:1px;overflow:hidden;' +
        'clip:rect(0 0 0 0);white-space:nowrap;">Your email address</label>' +
        '<input type="email" id="kuvr-iq-email" required placeholder="you@email.com">' +
        '<button type="submit" id="kuvr-iq-send">Send</button>';
      box.appendChild(form);
      ensureInlineQuoteStyles();
      alignToPriceBaseline();
      setTimeout(alignToPriceBaseline, 60);
      var input = form.querySelector('#kuvr-iq-email');
      setImp(input, {
        width: '190px', padding: '8px 10px', border: '1px solid #c9c9c9',
        'border-radius': '4px', 'font-family': 'inherit', 'font-size': '14px',
        'font-weight': '400', color: '#202223', 'line-height': '1.2',
        'letter-spacing': 'normal', 'text-transform': 'none', height: 'auto',
        display: 'block', margin: '0'
      });
      var btn = form.querySelector('#kuvr-iq-send');
      setImp(btn, {
        border: '0', 'border-radius': '4px', background: KUVR_TEAL, color: '#fff',
        padding: '9px 14px', 'font-family': 'inherit', 'font-size': '14px',
        'font-weight': '400', 'line-height': '1.2', cursor: 'pointer',
        'white-space': 'nowrap', 'letter-spacing': 'normal', height: 'auto',
        display: 'block'
      });
      input.focus();
      form.addEventListener('submit', function (ev2) {
        ev2.preventDefault();
        var email = String(input.value || '').trim();
        if (!email) return;
        sendLead({
          type: 'recovery',
          email: email,
          source: 'inline',
          quote_price: lastQuotePrice !== null ? String(lastQuotePrice) : undefined
        }, function (res) {
          // The API suppresses a repeat of an identical quote within 10 minutes
          // and sends no second email. Say so, rather than leave someone waiting
          // for mail that is not coming.
          // `done` is assigned synchronously just below; the callback is async, so
          // it exists by then. Guarded anyway for the synchronous refusal paths.
          if (res && res.deduped && done) done.textContent = 'Already sent — this quote is in your inbox.';
        });
        // Submitting here also settles the pop-up: someone who has just handed
        // over their address must not then be interrupted by a card asking for
        // it again (the outcome-aware half of TR-59).
        recoveryShown = true;
        stampRecoveryOutcome('submitted');
        box.innerHTML = '';
        var done = document.createElement('span');
        done.textContent = 'Sent — check your inbox.';
        setImp(done, { 'font-family': 'inherit', 'font-size': '15px', color: KUVR_TEAL, 'line-height': '1.2' });
        box.appendChild(done);
      });
    });
  }


  // ------------------------------------------------------------- TR-73 capture modal
  /**
   * One capture surface for BOTH quote entry points (the inline price-row control
   * and the recovery card).
   *
   * WHY THIS REPLACED TWO INLINE EMAIL FIELDS
   *   1. We collected an email and nothing else, so `person.first_name` was empty
   *      on most quote leads and every email opened "Hi," instead of "Hi Duncan,".
   *      Measured 2026-08-21: 9 of 13 quote-lead profiles had no first name.
   *      `sendLead` already accepted `name` — only the UI never asked for it.
   *   2. A typo in the address failed silently: the lead saved, the email bounced
   *      into nowhere, and the customer thought we had ignored them.
   *   3. There was nowhere to put the consent line, so the follow-up emails
   *      rested on a weaker basis than they needed to.
   *
   * Deliberately NOT a confirm-email field: retyping an address has poor
   * completion on mobile. Format validation plus a domain-typo suggestion catches
   * more real mistakes with less abandonment.
   */
  var KUVR_COMMON_DOMAINS = [
    'gmail.com', 'googlemail.com', 'hotmail.com', 'hotmail.co.uk', 'outlook.com',
    'live.co.uk', 'yahoo.com', 'yahoo.co.uk', 'icloud.com', 'me.com', 'aol.com',
    'btinternet.com', 'sky.com', 'talktalk.net', 'virginmedia.com'
  ];

  function kuvrEmailValid(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email);
  }

  /**
   * Damerau-Levenshtein (optimal string alignment), capped — short domains only.
   *
   * Plain Levenshtein scores a TRANSPOSITION as 2 (two substitutions), so
   * "gmial.com" sat at distance 2 from "gmail.com" and a <=1 threshold missed it
   * — i.e. the single most common typo class was exactly the one that got
   * through. Counting a transposition as 1 catches it while keeping the
   * threshold tight enough to avoid suggesting nonsense.
   */
  function kuvrEditDistance(a, b) {
    if (a === b) return 0;
    if (Math.abs(a.length - b.length) > 2) return 99;
    var la = a.length, lb = b.length, d = [], i, j;
    for (i = 0; i <= la; i++) { d[i] = []; d[i][0] = i; }
    for (j = 0; j <= lb; j++) { d[0][j] = j; }
    for (i = 1; i <= la; i++) {
      for (j = 1; j <= lb; j++) {
        var cost = a.charAt(i - 1) === b.charAt(j - 1) ? 0 : 1;
        d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
        if (i > 1 && j > 1 &&
            a.charAt(i - 1) === b.charAt(j - 2) &&
            a.charAt(i - 2) === b.charAt(j - 1)) {
          d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
        }
      }
    }
    return d[la][lb];
  }

  /** Returns a corrected address when the domain is one edit away from a common
   *  one ("gmial.com" -> "gmail.com"), else null. Never auto-applies. */
  function kuvrSuggestEmail(email) {
    var at = email.lastIndexOf('@');
    if (at < 1) return null;
    var local = email.slice(0, at), domain = email.slice(at + 1).toLowerCase();
    if (!domain) return null;
    for (var i = 0; i < KUVR_COMMON_DOMAINS.length; i++) {
      var d = KUVR_COMMON_DOMAINS[i];
      if (domain === d) return null;
      if (kuvrEditDistance(domain, d) <= 1) return local + '@' + d;
    }
    return null;
  }

  function kuvrModalStyles() {
    if (document.getElementById('kuvr-cap-styles')) return;
    var s = document.createElement('style');
    s.id = 'kuvr-cap-styles';
    s.textContent = [
      '.kuvr-cap-ov{position:fixed;inset:0;background:rgba(32,34,35,.55);z-index:2147483646;',
      'display:flex;align-items:center;justify-content:center;padding:16px;}',
      '.kuvr-cap{background:#fff!important;border-radius:10px;max-width:380px;width:100%;',
      'padding:24px;box-shadow:0 12px 40px rgba(0,0,0,.24);position:relative;box-sizing:border-box;',
      'font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif!important;',
      'text-align:left!important;letter-spacing:normal!important;text-transform:none!important;}',
      '.kuvr-cap *{box-sizing:border-box;font-family:inherit!important;letter-spacing:normal!important;',
      'text-transform:none!important;}',
      '.kuvr-cap h3{margin:0 0 6px!important;font-size:19px!important;font-weight:600!important;',
      'line-height:1.3!important;color:#202223!important;}',
      '.kuvr-cap p{margin:0 0 16px!important;font-size:14px!important;line-height:1.5!important;color:#6d7175!important;}',
      '.kuvr-cap label{display:block!important;font-size:13px!important;font-weight:600!important;',
      'color:#202223!important;margin:0 0 5px!important;line-height:1.3!important;}',
      '.kuvr-cap input{width:100%!important;padding:11px 12px!important;border:1px solid #c9c9c9!important;',
      'border-radius:6px!important;font-size:16px!important;line-height:1.2!important;color:#202223!important;',
      'height:auto!important;margin:0 0 12px!important;background:#fff!important;display:block!important;}',
      '.kuvr-cap input:focus{outline:none!important;border-color:' + KUVR_TEAL + '!important;',
      'box-shadow:0 0 0 3px rgba(0,111,108,.15)!important;}',
      '.kuvr-cap button.kuvr-cap-go{width:100%!important;border:0!important;border-radius:6px!important;',
      'background:' + KUVR_TEAL + '!important;color:#fff!important;padding:13px!important;font-size:15px!important;',
      'font-weight:600!important;cursor:pointer!important;line-height:1.2!important;height:auto!important;',
      'display:block!important;margin:4px 0 0!important;}',
      '.kuvr-cap .kuvr-cap-x{position:absolute;top:8px;right:10px;border:0;background:none;font-size:22px;',
      'line-height:1;cursor:pointer;color:#8c9196;padding:4px;}',
      '.kuvr-cap .kuvr-cap-note{font-size:12px!important;line-height:1.5!important;color:#8c9196!important;',
      'margin:12px 0 0!important;}',
      '.kuvr-cap .kuvr-cap-err{font-size:13px!important;color:#c0392b!important;margin:-8px 0 10px!important;',
      'line-height:1.4!important;display:none;}',
      '.kuvr-cap .kuvr-cap-err a{color:' + KUVR_TEAL + '!important;text-decoration:underline;cursor:pointer;}',
      '@media(max-width:480px){.kuvr-cap{padding:20px;}}'
    ].join('');
    document.head.appendChild(s);
  }

  /**
   * Ask the server whether the domain can receive email at all (MX lookup).
   *
   * The suggestion list above only knows ~15 consumer domains — it catches
   * `gmial.com` and misses `gmial.co.uk` or any mistyped company domain. This
   * is the general check: no mail servers means the address cannot work however
   * it is spelled. Fails OPEN, so a slow or broken lookup never costs a lead.
   *
   * It validates the DOMAIN, never the mailbox: `mikee@gmail.com` for `mike@`
   * is undetectable here, which is why the confirmation shows the address back.
   */
  function kuvrDomainReachable(email, cb) {
    var at = email.lastIndexOf('@');
    if (at < 1) { cb(true); return; }
    var domain = email.slice(at + 1).toLowerCase();
    var done = false;
    var t = setTimeout(function () { if (!done) { done = true; cb(true); } }, 3000);
    try {
      fetch(CONFIG.validateEmailEndpoint + '?domain=' + encodeURIComponent(domain), {
        method: 'GET', credentials: 'omit'
      })
        .then(function (r) { return r.json(); })
        .then(function (j) {
          if (done) return; done = true; clearTimeout(t);
          cb(!j || j.ok !== false, j && j.reason);
        })
        .catch(function () { if (!done) { done = true; clearTimeout(t); cb(true); } });
    } catch (e) { if (!done) { done = true; clearTimeout(t); cb(true); } }
  }

  /**
   * @param {string} source  'inline' | 'card'
   * @param {function} onSubmitted  called after a successful submission
   * @returns {boolean} false if the modal could not be built (caller falls back)
   */
  function openQuoteCaptureModal(source, onSubmitted) {
    try {
      if (document.querySelector('.kuvr-cap-ov')) return true;
      kuvrModalStyles();

      var ov = document.createElement('div');
      ov.className = 'kuvr-cap-ov';
      ov.setAttribute('role', 'dialog');
      ov.setAttribute('aria-modal', 'true');
      ov.innerHTML =
        '<div class="kuvr-cap">' +
          '<button type="button" class="kuvr-cap-x" aria-label="Close">&times;</button>' +
          '<h3>Email me my quote</h3>' +
          '<p>We’ll send your exact size and price, so you can pick up where you left off.</p>' +
          '<form novalidate>' +
            '<label for="kuvr-cap-name">First name</label>' +
            '<input id="kuvr-cap-name" type="text" autocomplete="given-name" autocapitalize="words" required>' +
            // 2026-09-12: last name added. A first-name-only lead reached Klaviyo,
            // whose Shopify sync then wrote a customer with no surname (BUG
            // 2026-09-12 in kuvrnexus-app). Full name goes out as `name` below.
            '<label for="kuvr-cap-last">Last name</label>' +
            '<input id="kuvr-cap-last" type="text" autocomplete="family-name" autocapitalize="words" required>' +
            '<label for="kuvr-cap-email">Email address</label>' +
            '<input id="kuvr-cap-email" type="email" autocomplete="email" inputmode="email" placeholder="you@email.com" required>' +
            '<div class="kuvr-cap-err"></div>' +
            '<button type="submit" class="kuvr-cap-go">Send my quote</button>' +
          '</form>' +
          '<p class="kuvr-cap-note">We’ll send your quote plus a couple of helpful follow-ups. ' +
          'Opt out anytime.</p>' +
        '</div>';
      document.body.appendChild(ov);

      var form  = ov.querySelector('form');
      var nameI = ov.querySelector('#kuvr-cap-name');
      var lastI = ov.querySelector('#kuvr-cap-last');
      var mailI = ov.querySelector('#kuvr-cap-email');
      var err   = ov.querySelector('.kuvr-cap-err');
      var card  = ov.querySelector('.kuvr-cap');
      var prevFocus = document.activeElement;

      function showErr(html) { err.innerHTML = html; err.style.display = 'block'; }
      function clearErr() { err.innerHTML = ''; err.style.display = 'none'; }

      function close(reason) {
        if (!ov.parentNode) return;
        ov.parentNode.removeChild(ov);
        document.removeEventListener('keydown', onKey);
        try { prevFocus && prevFocus.focus && prevFocus.focus(); } catch (e) {}
        if (reason === 'dismissed') {
          // An explicit no. Same treatment as closing the card (TR-59).
          stampRecoveryOutcome('dismissed');
          track('quote_recovery_dismissed', { source: source });
        }
      }
      function onKey(e) { if (e.key === 'Escape' || e.keyCode === 27) close('dismissed'); }

      ov.querySelector('.kuvr-cap-x').addEventListener('click', function () { close('dismissed'); });
      ov.addEventListener('mousedown', function (e) { if (e.target === ov) close('dismissed'); });
      document.addEventListener('keydown', onKey);
      // Clicking inside must never bubble out to page handlers behind the overlay.
      card.addEventListener('mousedown', function (e) { e.stopPropagation(); });

      setTimeout(function () { try { nameI.focus(); } catch (e) {} }, 30);

      form.addEventListener('submit', function (ev) {
        ev.preventDefault();
        clearErr();
        var first = String(nameI.value || '').replace(/\s+/g, ' ').trim();
        var last  = String(lastI.value || '').replace(/\s+/g, ' ').trim();
        var name  = (first + ' ' + last).trim();
        var email = String(mailI.value || '').trim();

        if (!first) { showErr('Please tell us your first name.'); try { nameI.focus(); } catch (e) {} return; }
        if (!last)  { showErr('Please tell us your last name.');  try { lastI.focus(); } catch (e) {} return; }
        if (!kuvrEmailValid(email)) {
          showErr('That email address does not look right — please check it.');
          try { mailI.focus(); } catch (e) {}
          return;
        }
        var fix = kuvrSuggestEmail(email);
        if (fix && mailI.getAttribute('data-accepted') !== '1') {
          // Offer once. If they insist, the second submit goes through unchanged.
          showErr('Did you mean <a data-fix="1">' + fix + '</a>? ' +
                  '<a data-keep="1">No, use what I typed</a>');
          err.querySelector('[data-fix]').addEventListener('click', function () {
            mailI.value = fix; mailI.setAttribute('data-accepted', '1'); clearErr();
            form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
          });
          err.querySelector('[data-keep]').addEventListener('click', function () {
            mailI.setAttribute('data-accepted', '1'); clearErr();
            form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
          });
          return;
        }

        var go = form.querySelector('.kuvr-cap-go');
        go.disabled = true; go.textContent = 'Checking…';

        kuvrDomainReachable(email, function (reachable) {
          if (!reachable) {
            go.disabled = false; go.textContent = 'Send my quote';
            showErr('We can’t find an email provider at “' +
              email.slice(email.lastIndexOf('@') + 1).replace(/[<>&]/g, '') +
              '”. Please check the address.');
            try { mailI.focus(); } catch (e) {}
            return;
          }
          go.textContent = 'Sending…';
          submitLead();
        });
        return;

        function submitLead() {
        sendLead({
          type: 'recovery',
          email: email,
          name: name,
          source: source,
          quote_price: lastQuotePrice !== null ? String(lastQuotePrice) : undefined
        }, function (res) {
          var head = card.querySelector('h3'), body = card.querySelector('p');
          if (res && res.deduped) {
            if (head) head.textContent = 'Already sent';
            if (body) body.textContent = 'This quote is already in your inbox.';
          }
        });

        // Someone who has just handed over their address must not then be
        // interrupted by the card asking for it again (TR-59).
        recoveryShown = true;
        stampRecoveryOutcome('submitted');

        // Show the address back. A mistyped MAILBOX ("mikee" for "mike") passes
        // every check there is — the customer seeing it is the only defence.
        card.innerHTML =
          '<h3>Sent — check your inbox.</h3>' +
          '<p>Your quote is on its way' + (first ? ', ' + first.replace(/[<>&]/g, '') : '') +
          '.</p><p style="font-size:13px!important;color:#8c9196!important;">Sent to <strong>' +
          email.replace(/[<>&]/g, '') + '</strong> — not right? ' +
          '<a href="#" class="kuvr-cap-retry" style="color:' + KUVR_TEAL + ';">Try another address</a></p>';
        var retry = card.querySelector('.kuvr-cap-retry');
        if (retry) retry.addEventListener('click', function (rev) {
          rev.preventDefault();
          close('submitted');
          setTimeout(function () { openQuoteCaptureModal(source, onSubmitted); }, 60);
        });
        if (typeof onSubmitted === 'function') { try { onSubmitted(); } catch (e) {} }
        setTimeout(function () { close('submitted'); }, 9000);
        }
      });

      return true;
    } catch (e) {
      // Capture must never break because a modal failed to build.
      try { console.error('[kuvr] capture modal failed, falling back:', e); } catch (e2) {}
      return false;
    }
  }

  // ---------------------------------------------------- TR-59 outcome-aware cap
  // The old rule capped for 7 days the moment the card was DISPLAYED, keyed per
  // browser, and treated ignored / dismissed / submitted identically. So someone
  // who handed over their address was silenced exactly like someone who closed
  // it, and someone who ignored it once on one product never saw it on another.
  // Outcome now decides the window, and the window is per product.
  var QR_STATE_KEY = '_kuvr_qr_state';
  var QR_WINDOWS = {
    ignored: 6 * 3600000,           // shown, walked away — try again later today
    dismissed: 7 * 86400000,        // actively closed — a clear no, on this product
    dismissed_global: 24 * 3600000, // and a quiet day across every other product
    submitted: 3650 * 86400000      // gave us the address — never ask again
  };

  function readQrState() {
    try { return JSON.parse(localStorage.getItem(QR_STATE_KEY) || '{}') || {}; }
    catch (e) { return {}; }
  }

  function stampRecoveryOutcome(outcome) {
    try {
      var st = readQrState();
      // A submission is global: having our address means we never ask again on
      // ANY product, not just this one.
      if (outcome === 'submitted') {
        st._all = { o: 'submitted', t: Date.now() };
      } else {
        st[PRODUCT_ID || '_'] = { o: outcome, t: Date.now() };
        // A dismissal is a signal about the OFFER, not about this product. Left
        // purely per-product, someone who closes the card then opens a second
        // product is asked again immediately, which is exactly the pestering the
        // cap exists to prevent. So a dismissal also buys a short quiet period
        // everywhere, while still allowing a genuinely new visit days later.
        if (outcome === 'dismissed') st._all = { o: 'dismissed_global', t: Date.now() };
      }
      localStorage.setItem(QR_STATE_KEY, JSON.stringify(st));
    } catch (e) {}
  }

  function capEntryOk(entry) {
    if (!entry || !entry.t) return true;
    var win = QR_WINDOWS[entry.o];
    if (win === undefined) win = QR_WINDOWS.dismissed;
    return (Date.now() - entry.t) > win;
  }

  function recoveryCapOk() {
    var st = readQrState();
    if (!capEntryOk(st._all)) return false;             // submitted anywhere
    if (!capEntryOk(st[PRODUCT_ID || '_'])) return false;
    // Legacy key from the pre-TR-59 build — honour it as a dismissal so anyone
    // already capped is not suddenly re-prompted by the upgrade.
    try {
      var last = parseInt(localStorage.getItem('_kuvr_qr_last') || '0', 10);
      if (last && (Date.now() - last) <= QR_WINDOWS.dismissed) return false;
    } catch (e) {}
    return true;
  }

  // Kept for callers that just mean "do not ask again for a while".
  function stampRecoveryCap() { stampRecoveryOutcome('dismissed'); }

  function buildRecoveryPrompt() {
    var wrap = document.createElement('div');
    wrap.id = 'kuvr-quote-recovery';
    wrap.setAttribute('role', 'dialog');
    wrap.setAttribute('aria-label', 'Email me my quote');
    wrap.style.cssText = 'position:fixed;bottom:16px;right:16px;z-index:99999;' +
      'max-width:320px;background:#fff;border:1px solid #ddd;border-radius:10px;' +
      'box-shadow:0 6px 24px rgba(0,0,0,.18);padding:16px;' +
      'font:14px/1.4 -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;color:#222;';
    wrap.innerHTML =
      '<button type="button" id="kuvr-qr-close" aria-label="Close" style="position:absolute;' +
      'top:6px;right:10px;border:0;background:none;font-size:18px;cursor:pointer;color:#888;">&times;</button>' +
      '<div style="font-weight:600;margin-bottom:6px;">Want your quote emailed to you?</div>' +
      '<div style="margin-bottom:10px;color:#555;">We’ll send your exact size &amp; price so you can pick up where you left off.</div>' +
      // TR-73: the card is a BUTTON only. It used to carry its own email field,
      // which meant the customer typed an address here and was then asked for it
      // again in the modal — so the card was still the old email-only capture in
      // everything but name. One button, one modal, one place to type.
      // The fallback form is injected in its place only if the modal fails.
      '<button type="button" id="kuvr-qr-open" style="width:100%;padding:11px 12px;border:0;' +
      'border-radius:6px;background:#006F6C;color:#fff;font-size:15px;font-weight:600;' +
      'cursor:pointer;white-space:nowrap;line-height:1.2;">Email me my quote</button>';
    return wrap;
  }

  function showRecoveryPrompt(trigger) {
    if (recoveryShown || !lastQuoteKey) return; // never pre-quote
    if (!recoveryCapOk()) return;
    if (document.getElementById('kuvr-quote-recovery')) return;
    recoveryShown = true;
    stampRecoveryOutcome('ignored'); // upgraded on dismiss or submit below
    var el = buildRecoveryPrompt();
    document.body.appendChild(el);
    track('quote_recovery_shown', { trigger: trigger });

    el.querySelector('#kuvr-qr-close').addEventListener('click', function () {
      stampRecoveryOutcome('dismissed'); // an explicit no, not just a non-answer
      track('quote_recovery_dismissed', {});
      el.parentNode && el.parentNode.removeChild(el);
    });
    el.querySelector('#kuvr-qr-open').addEventListener('click', function () {
      if (openQuoteCaptureModal('card', function () {
        el.parentNode && el.parentNode.removeChild(el);
      })) return;
      // Fallback only: the modal could not build, so put the old inline field
      // back in the card rather than leaving a button that does nothing.
      var host = el.querySelector('#kuvr-qr-open');
      if (!host) return;
      host.outerHTML =
        '<form id="kuvr-qr-form" style="display:flex;gap:6px;">' +
        '<input type="email" id="kuvr-qr-email" required placeholder="you@email.com" ' +
        'style="flex:1;min-width:0;padding:8px 10px;border:1px solid #ccc;border-radius:6px;font-size:14px;">' +
        '<button type="submit" style="padding:8px 12px;border:0;border-radius:6px;background:#006F6C;' +
        'color:#fff;font-size:14px;font-weight:700;cursor:pointer;white-space:nowrap;">Email me</button>' +
        '</form>';
      el.querySelector('#kuvr-qr-form').addEventListener('submit', function (ev) {
      ev.preventDefault();
      var email = String(el.querySelector('#kuvr-qr-email').value || '').trim();
      if (!email) return;
      sendLead({
        type: 'recovery',
        email: email,
        source: 'card',
        quote_price: lastQuotePrice !== null ? String(lastQuotePrice) : undefined
      }, function (res) {
        // Same as the inline control: a suppressed duplicate must not be reported
        // as a send. The node is replaced below, so re-read it rather than caching.
        if (res && res.deduped && el) {
          el.innerHTML = '<div style="font-weight:600;">Already sent — this quote is in your inbox.</div>';
        }
      });
      stampRecoveryOutcome('submitted');
      el.innerHTML = '<div style="font-weight:600;">Sent! Check your inbox shortly.</div>';
      setTimeout(function () { el.parentNode && el.parentNode.removeChild(el); }, 4000);
      });
    });
  }

  function resetRecoveryIdle() {
    if (!recoveryArmed || recoveryShown) return;
    if (recoveryIdleTimer) clearTimeout(recoveryIdleTimer);
    recoveryIdleTimer = setTimeout(function () {
      showRecoveryPrompt('idle');
    }, CONFIG.recoveryIdleMs);
  }

  function armQuoteRecovery() {
    if (recoveryArmed) { resetRecoveryIdle(); return; }
    recoveryArmed = true;
    ['scroll', 'mousemove', 'keydown', 'click', 'touchstart'].forEach(function (evName) {
      document.addEventListener(evName, resetRecoveryIdle, { passive: true, capture: true });
    });
    resetRecoveryIdle();
    // Desktop exit-intent: pointer leaves through the top of the viewport.
    document.addEventListener('mouseout', function (ev) {
      if (!ev.relatedTarget && ev.clientY <= 0) showRecoveryPrompt('exit_intent');
    });
  }

  // --------------------------------------- expert-contact path (TR-36, §5.6)
  // ask_expert_clicked on recognisable "ask an expert" affordances; an
  // expert lead from the theme's contact form when one is submitted.
  document.addEventListener('click', function (ev) {
    var t = ev.target && ev.target.closest && ev.target.closest(
      '[data-kuvr-expert], #ask-expert, .ask-expert, a[href*="ask-an-expert"], a[href^="/pages/contact"]'
    );
    if (t) track('ask_expert_clicked', { via: t.id || t.className || t.getAttribute('href') || 'unknown' });
  }, true);

  // 🛑 Shopify renders EVERY {% form 'customer' %} with action="/contact#..." — the
  // 10%-off popup and the footer newsletter signup included. Matching on the action
  // alone therefore turned every newsletter subscriber into an "expert" lead, and
  // inflated the Kuvr Expert Requested metric with people who had never asked to speak
  // to anyone. Found 2026-09-11 after a customer was wrongly described to Duncan as
  // having requested an expert; see
  // .planning/BUG-2026-09-11-expert-lead-fires-on-newsletter-signup.md.
  //
  // Tell the two apart STRUCTURALLY, not by content: a real enquiry form has a message
  // field, a newsletter signup is an email box and nothing else. Test for the FIELD's
  // existence rather than for a filled-in message, so a genuine enquiry sent with an
  // empty message is still captured. Shopify also tags signups contact[tags]=newsletter
  // — checked as well, because a theme can render the field without the tag and vice
  // versa. Either signal alone is enough to reject.
  function isEnquiryForm(form, fd) {
    var tags = String((fd && fd.get('contact[tags]')) || '').toLowerCase();
    if (tags.indexOf('newsletter') !== -1) return false;
    // 🛑 Kuvr's own enquiry form is NOT a Shopify customer form. #kuvr-contact-form
    // posts multipart/form-data to nexus.kuvr.co/api/contact (which opens a Help Scout
    // conversation in mailbox 320630) using PLAIN field names — name / email / phone /
    // message — so none of the contact[...] tests below ever matched it, and no lead was
    // ever created from a genuine enquiry. See
    // .planning/FEAT-2026-09-11-contact-form-enquiry-leads.md.
    if (form.id === 'kuvr-contact-form') return true;
    return !!(form.querySelector && form.querySelector('[name="contact[body]"]'));
  }

  // Bot filter (2026-09-23 form-bombing run, kuvrnexus-app/.planning/incidents/
  // 2026-09-23-contact-form-bot-bombing.md). A bot filled the contact form with real
  // third-party emails and random-letter names; this listener turned each hit into an
  // expert lead, a Klaviyo profile (identifyToKlaviyo) and, via Klaviyo's Shopify sync,
  // a junk Shopify customer. Same rule as nexus /api/contact (src/lib/contact-spam.ts):
  // the hidden kv_trap field is filled, or any word of 8+ letters has 3+ lower->UPPER flips.
  function looksLikeBotText(text) {
    var words = String(text || '').split(/[^A-Za-z]+/);
    for (var i = 0; i < words.length; i++) {
      var w = words[i], flips = 0;
      if (w.length < 8) continue;
      for (var j = 1; j < w.length; j++) {
        if (/[a-z]/.test(w[j - 1]) && /[A-Z]/.test(w[j])) flips++;
      }
      if (flips >= 3) return true;
    }
    return false;
  }

  document.addEventListener('submit', function (ev) {
    var form = ev.target;
    if (!form || !form.action || form.action.indexOf('/contact') === -1) return;
    var email = '';
    var name = '';
    var enquiry = false;
    var bot = false;
    try {
      var fd = new FormData(form);
      // Shopify customer forms use contact[...]; Kuvr's enquiry form uses plain names —
      // and since 2026-09-12 separate first_name / last_name, with no `name` field at all
      // (which is why contact-page leads reached Klaviyo nameless, BUG-2026-09-12).
      email = String(fd.get('contact[email]') || fd.get('email') || '').trim();
      var joined = (String(fd.get('first_name') || '') + ' ' + String(fd.get('last_name') || '')).trim();
      name = String(fd.get('contact[name]') || fd.get('name') || joined || '').trim();
      enquiry = isEnquiryForm(form, fd);
      bot = !!String(fd.get('kv_trap') || '') ||
        looksLikeBotText(name + ' ' + String(fd.get('message') || fd.get('contact[body]') || ''));
    } catch (e) { /* FormData unsupported — skip lead */ }
    if (bot) return;
    if (email && enquiry) sendLead({ type: 'expert', email: email, name: name || undefined });
  }, true);

  // ---------------------------------------------------------------- start-up
  if (trackingActive()) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', startBehaviouralTracking);
    } else {
      startBehaviouralTracking();
    }
  }
  // Essential tier: stamp the cart as soon as anything is added even without
  // behavioural consent — wire the ATC listener unconditionally.
  document.addEventListener('submit', function (ev) {
    var form = ev.target;
    if (form && form.action && form.action.indexOf('/cart/add') !== -1) stampCart();
  }, true);
})();
